<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6">

    <!-- SIV Form Card -->
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] print-hide">
      <div class="px-6 pt-5 pb-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center justify-between gap-3 min-w-0">
          <h2 class="text-base font-bold text-[#0d4169] text-left">Store Issue Voucher (SIV)</h2>
          <span v-if="loading" class="text-xs text-slate-400 animate-pulse text-right sm:hidden shrink-0">Loading data...</span>
        </div>
        <div class="flex items-center justify-end gap-3">
          <label class="inline-flex items-center gap-2 text-[0.78rem] font-semibold text-[#1e293b] cursor-pointer select-none">
            <input
              v-model="isOtherTransfer"
              type="checkbox"
              :disabled="isOtherTransferHeaderLocked"
              class="h-3.5 w-3.5 rounded border-[#cbd5e1] text-[#0d4169] focus:ring-[#0d4169] disabled:opacity-60 disabled:cursor-not-allowed"
            >
            Transfer To Other Branchs
          </label>
          <span v-if="loading" class="hidden sm:inline text-xs text-slate-400 animate-pulse text-right">Loading data...</span>
        </div>
      </div>
      <form @submit.prevent="processSiv" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2 text-left">

          <!-- SIV Number (display-only) -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">{{ sivNumberLabel }}</label>
            <input
              :value="displayedSivNumber"
              type="text"
              readonly
              class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed"
            >
          </div>

          <!-- Transaction Date -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Transaction Date</label>
            <input v-model="form.date" type="date"
              :min="isPendingEditMode ? null : todayDate"
              :max="isPendingEditMode ? null : todayDate"
              :disabled="isApprovalSelectionRequiredLock || (isPendingEditMode && !canEditTransactionDate)"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
              required>
          </div>

          <!-- Other Transfer: destination branch / head office -->
          <div v-if="isOtherTransfer" class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Branch</label>
            <select
              v-model="otherTransferTargetKey"
              :disabled="isApprovalSelectionRequiredLock || isOtherTransferHeaderLocked || isApprovedArrowLock"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              required
            >
              <option :value="null" disabled>Select Branch</option>
              <option v-for="opt in otherTransferBranchOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
            </select>
          </div>

          <!-- Department -->
          <div class="flex flex-col gap-1" v-if="!isOtherTransfer">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Issuing To Department</label>
            <select
              v-model="form.issuingToKey"
              :disabled="isApprovalSelectionRequiredLock || isIssuingToLocked || isApprovedArrowLock || (isPendingEditMode && !canEditDepartment) || (isOtherTransfer && !otherTransferTargetKey)"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              :required="!isOtherTransfer"
            >
              <option :value="null" disabled>{{ isOtherTransfer ? 'Select Department' : 'Select Department/Category' }}</option>
              <option v-for="opt in visibleIssuingToOptions" :key="opt.key" :value="opt.key">{{ opt.name }}</option>
            </select>
          </div>

          <!-- Category (normal SIV: stays in the header grid) -->
          <div v-if="!isOtherTransfer" class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Category</label>
            <select v-model="form.categoryId"
              :disabled="isApprovalSelectionRequiredLock || isApprovedArrowLock || (isPendingEditMode && !canEditCategory)"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null" disabled>Select Category</option>
              <option v-for="cat in selectableCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>

          <!-- Other Transfer: Category + Item on one row at every screen size -->
          <div v-if="isOtherTransfer" class="col-span-2 md:col-span-3 lg:col-span-4 w-full min-w-0 grid grid-cols-2 gap-x-4 gap-y-2">
            <div class="flex flex-col gap-1 min-w-0">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Category</label>
              <select v-model="form.categoryId"
                :disabled="isApprovalSelectionRequiredLock || isApprovedArrowLock || (isPendingEditMode && !canEditCategory)"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
                <option :value="null" disabled>Select Category</option>
                <option v-for="cat in selectableCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
              </select>
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Item</label>
              <select v-model="form.selectedItemKey"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
                :disabled="isApprovalSelectionRequiredLock || !form.categoryId || isApprovedArrowLock || (isPendingEditMode && !canEditItemField)" required>
                <option :value="null" disabled>Select Item</option>
                <option 
                  v-for="opt in filteredSelectableItems" 
                  :key="opt.key" 
                  :value="opt.key"
                  :style="opt.type === 'product' ? 'color:#16a34a; font-weight:600;' : ''"
                >
                  {{ opt.name }} ({{ opt.unit }})
                </option>
              </select>
            </div>
          </div>

          <!-- Item + Current Balance + Remark (normal: item+balance+remark; other transfer: balance+remark only) -->
          <div
            class="col-span-2 md:col-span-3 lg:col-span-4 w-full min-w-0 flex gap-4"
            :class="isOtherTransfer ? 'siv-ot-layout flex-col lg:flex-row' : 'flex-col md:flex-row'"
          >
            <!-- Item (normal SIV only; other transfer item is in the row above) -->
            <div v-if="!isOtherTransfer" class="siv-field-item flex-1 min-w-0 flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Item</label>
              <select v-model="form.selectedItemKey"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
                :disabled="isApprovalSelectionRequiredLock || !form.categoryId || isApprovedArrowLock || (isPendingEditMode && !canEditItemField)" required>
                <option :value="null" disabled>Select Item</option>
                <option 
                  v-for="opt in filteredSelectableItems" 
                  :key="opt.key" 
                  :value="opt.key"
                  :style="opt.type === 'product' ? 'color:#16a34a; font-weight:600;' : ''"
                >
                  {{ opt.name }} ({{ opt.unit }})
                </option>
              </select>
            </div>

            <!-- Approved Quantity (Read-only, shown when item added from approval) -->
            <div v-if="form.approvedRequestItemId" class="siv-field-approved flex-1 min-w-0 flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-emerald-600">Approved Quantity</label>
              <div class="px-2.5 py-1.5 border border-emerald-200 bg-emerald-50 rounded-md text-sm font-bold text-emerald-700">
                {{ form.approvedQuantity }} {{ selectedItemUnit }}
              </div>
            </div>

            <!-- Current Balance Panel -->
            <div class="siv-field-balance w-full min-w-0 flex-1 flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">
                Current Balance
                <span v-if="balanceLoading" class="ml-2 text-slate-400 animate-pulse text-xs font-normal">Loading...</span>
              </label>

              <!-- No item selected -->
              <div v-if="!form.selectedItemKey" class="text-xs text-slate-400 italic px-2">
                Select an item to see its current stock balance.
              </div>

              <!-- Loading state after item selection -->
              <div v-else-if="balanceLoading" class="text-xs text-slate-400 italic px-2">
                Loading stock balance...
              </div>

              <!-- Balance rows table (header only when item selected and not loading) -->
              <div v-else class="siv-balance-scroll w-full min-w-0 border border-[#cbd5e1] rounded-md">
                <table class="siv-balance-table w-full">
                  <thead>
                    <tr class="bg-slate-50">
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-3 py-1.5">Balance</th>
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-3 py-1.5">Unit Price</th>
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-3 py-1.5">Volume</th>
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-3 py-1.5">Expiry Date</th>
                      <th class="text-left text-[0.68rem] font-semibold text-slate-600 px-3 py-1.5">Qty to Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="stockBalances.length === 0 && !balanceLoading">
                      <td colspan="5" class="px-3 py-1.5 text-xs text-amber-600 italic">
                        No stock available for this item.
                      </td>
                    </tr>
                    <tr v-else v-for="(row, idx) in stockBalances" :key="idx" class="border-t border-slate-100 hover:bg-slate-50">
                      <td class="px-3 py-1.5 text-xs font-semibold" :class="row.balance <= 0 ? 'text-red-500' : 'text-emerald-600'">
                        {{ formatBalanceQty(row.balance) }} {{ selectedItemUnit }}
                      </td>
                      <td class="px-3 py-1.5 text-xs text-slate-700">
                        {{ row.unitPrice.toFixed(2) }}
                      </td>
                      <td class="px-3 py-1.5 text-xs text-slate-700">
                        {{ row.volume }}
                      </td>
                      <td class="px-3 py-1.5 text-xs text-slate-700">
                        {{ row.expiryDate || '-' }}
                      </td>
                      <td class="px-3 py-1.5">
                        <input
                          v-model.number="row.issueQty"
                          type="number"
                          min="0"
                           step="any"
                      inputmode="decimal"
                          :max="(isPendingEditMode ? (Number(row.originalIssueQty || 0) + Number(row.balance || 0)) : row.balance)"
                          placeholder="0"
                          :disabled="isApprovalSelectionRequiredLock || (!isPendingEditMode && row.balance <= 0) || (isPendingEditMode && !canEditQtyAt(idx))"
                          class="w-24 px-2 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-xs disabled:opacity-40"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Remark -->
            <div class="siv-field-remark flex-1 min-w-0 flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Issuer Remark</label>
              <input
                v-model="form.remark"
                type="text"
                placeholder="e.g. Approved by HR..."
                :disabled="isApprovalSelectionRequiredLock || isApprovedArrowLock || (isPendingEditMode && !canEditRemark)"
                class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
              >
            </div>
          </div>

        </div>

        <!-- Action Buttons -->
        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-end gap-3">
          <button v-if="isEditing" type="button" @click="cancelEdit"
            class="px-5 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-300 transition-all">Cancel</button>
          <button v-if="!isOtherTransfer && selectedItemIsScannable && (!isEditing || editSource === 'items')" type="button" @click="openScanModal"
            :disabled="(!isEditing && isApprovalSelectionRequiredLock) || (!isEditing && !canAddToSivList) || balanceLoading"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isEditing ? 'Scan Update' : 'Scan Item' }}
          </button>
          <button v-else type="submit"
            :disabled="isApprovalSelectionRequiredLock || (!isEditing && !canAddToSivList)"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isEditing ? 'Update SIV' : 'Add to List' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Approved Requests (TODAY) - show above SIV list -->
    <div v-if="!isOtherTransfer && todayApprovedList.length > 0" class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 print-hide">
      <h3 class="font-bold text-sm mb-2">Approved Store Request Items (Today)</h3>
      <div class="w-full overflow-x-auto">
        <table class="w-full border-collapse bg-white">
          <thead>
            <tr>
              <th>#</th>
              <th>Req. Date</th>
              <th>Req.Department</th>
              <th>Req. name/user</th>
              <th>Req. number</th>
              <th>Category</th>
              <th>Item</th>
              <th>U.O.M</th>
              <th>Req. Qty</th>
              <th>Appr. Qty</th>
              <th>Approved By</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(a, idx) in todayApprovedList" :key="a.id">
              <td>{{ idx + 1 }}</td>
              <td>{{ a.transactionDate }}</td>
              <td>{{ a.department }}</td>
              <td>{{ a.requester }}</td>
              <td>{{ a.srNumber }}</td>
              <td>{{ a.category }}</td>
              <td>{{ a.item }}</td>
              <td>{{ a.uom }}</td>
              <td>{{ a.requestedQuantity }} {{ a.uom }}</td>
              <td>{{ a.approvedQuantity }} {{ a.uom }}</td>
              <td>{{ a.approverName }}</td>
              <td><button @click="addApprovedToList(a)" class="px-2 py-1 text-blue-500 bg-blue-50 hover:bg-blue-100 rounded transition">→</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pending SIV Receive Approvals -->
    <div v-if="pendingReceiveApprovals.length > 0" class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 print-hide">
      <h3 class="font-bold text-sm mb-2">Pending SIV Receive Approvals</h3>
      <div class="w-full overflow-x-auto">
        <table class="w-full border-collapse bg-white">
          <thead>
            <tr>
              <th>#</th>
              <th>Siv No</th>
              <th>Date</th>
              <th>Department</th>
              <th>Category</th>
              <th>Item</th>
              <th>U.O.M</th>
              <th>Req. Qty</th>
              <th>Appr. Qty</th>
              <th>Qty</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in pendingReceiveApprovals" :key="r.id">
              <td>{{ idx + 1 }}</td>
              <td>{{ r.sivNo }}</td>
              <td>{{ r.date }}</td>
              <td>{{ r.department }}</td>
              <td>{{ r.category }}</td>
              <td class="font-semibold text-[#0d4169]">{{ r.item }}</td>
              <td>{{ r.uom || '-' }}</td>
              <td class="font-bold">{{ r.requestedQty ?? '-' }}</td>
              <td class="font-bold">{{ r.approvedQty ?? '-' }}</td>
              <td class="font-bold">{{ r.qty }}</td>
              <td>
                <button
                  v-if="Array.isArray(r.barcodeResults) && r.barcodeResults.some(result => Number(result.itemId) === Number(r.itemId))"
                  @click="openReceiveApprovalScan(r)"
                  :disabled="approvingReceiveIds.has(r.id) || !canApproveSivReceive"
                  class="px-2 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded-md hover:bg-emerald-600 transition-all shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ approvingReceiveIds.has(r.id) ? 'Approving...' : 'Approve by scan' }}
                </button>
                <button
                  v-else
                  @click="approveReceive(r.id)"
                  :disabled="approvingReceiveIds.has(r.id) || !canApproveSivReceive"
                  class="px-2 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded-md hover:bg-emerald-600 transition-all shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ approvingReceiveIds.has(r.id) ? 'Approving...' : 'Approve' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- My Created Not Approved SIV Items -->
    <div v-if="myPendingReceiveItems.length > 0" class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 print-hide">
      <h3 class="font-bold text-sm mb-2">Not Approved Internal SIV Items</h3>
      <div class="w-full overflow-x-auto">
        <table class="w-full border-collapse bg-white">
          <thead>
            <tr>
              <th>#</th>
              <th>Siv No</th>
              <th>Date</th>
              <th>Department</th>
              <th>Category</th>
              <th>Item</th>
              <th>U.O.M</th>
              <th>Req. Qty</th>
              <th>Appr. Qty</th>
              <th>Qty</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in myPendingReceiveItems" :key="r.id">
              <td>{{ idx + 1 }}</td>
              <td>{{ r.sivNo }}</td>
              <td>{{ r.date }}</td>
              <td>{{ r.department }}</td>
              <td>{{ r.category }}</td>
              <td class="font-semibold text-[#0d4169]">{{ r.item }}</td>
              <td>{{ r.uom || '-' }}</td>
              <td class="font-bold">{{ r.requestedQty ?? '-' }}</td>
              <td class="font-bold">{{ r.approvedQty ?? '-' }}</td>
              <td class="font-bold">{{ r.qty }}</td>
              <td class="font-bold text-amber-600">{{ r.status || 'Not Approved' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Not Approved Other SIV Items -->
    <div v-if="myNotApprovedOtherSivItems.length > 0" class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 print-hide">
      <h3 class="font-bold text-sm mb-2">Not Approved Other SIV Items</h3>
      <div class="w-full overflow-x-auto">
        <table class="w-full border-collapse bg-white">
          <thead>
            <tr>
              <th>#</th>
              <th>Siv No</th>
              <th>Date</th>
              <th>Branch</th>
              <th>Department</th>
              <th>Category</th>
              <th>Item</th>
              <th>U.O.M</th>
              <th>Req. Qty</th>
              <th>Appr. Qty</th>
              <th>Qty</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in myNotApprovedOtherSivItems" :key="r.id">
              <td>{{ idx + 1 }}</td>
              <td>{{ r.sivNo }}</td>
              <td>{{ r.date }}</td>
              <td>{{ r.branch || '-' }}</td>
              <td>{{ r.department }}</td>
              <td>{{ r.category }}</td>
              <td class="font-semibold text-[#0d4169]">{{ r.item }}</td>
              <td>{{ r.uom || '-' }}</td>
              <td class="font-bold">{{ r.requestedQty ?? '-' }}</td>
              <td class="font-bold">{{ r.approvedQty ?? '-' }}</td>
              <td class="font-bold">{{ r.qty }}</td>
              <td class="font-bold text-amber-600">{{ r.status || 'Not Approved' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- SIV List Table -->
    <div v-if="sivList.length > 0" class="flex flex-col gap-2">

      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <!-- Print Header (Hidden unless printing) -->
        <div class="hidden print:block mb-6">
          <div class="print-doc-header print-doc-header-grid">
            <div class="print-doc-left">
              <div class="print-company-block">
                <img
                  v-if="printCompanyLogo"
                  :src="printCompanyLogo"
                  :alt="printCompanyName || 'Company Logo'"
                  class="print-company-logo"
                />
                <div v-else class="print-company-logo-fallback">Logo</div>
              </div>
            </div>
            <div class="print-doc-title-wrap">
              <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
              <h1 class="print-doc-title text-xl font-bold text-center mb-2">{{ isOtherTransfer ? 'Other Store Issue Voucher' : 'Store Issue Voucher' }}</h1>
            </div>
            <div class="print-doc-meta print-doc-right">
              <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
            </div>
          </div>
          <hr class="my-4 border-black" />
        </div>

        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Siv No</th>
              <th>Date</th>
              <th v-if="isOtherTransfer">Branch</th>
              <th>Department</th>
              <th>Category</th>
              <th>Item</th>
              <th>Expiry Date</th>
              <th>Volume</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Total price</th>
              <th>Remark</th>
              <th class="print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in flattenedSivList" :key="index" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <td>{{ row.sivNumber }}</td>
              <td>{{ row.date }}</td>
              <td v-if="isOtherTransfer">{{ row.branchName || '-' }}</td>
              <td>{{ row.departmentName }}</td>
              <td>{{ row.categoryName }}</td>
              <td class="font-semibold text-[#0d4169]">{{ row.itemName }}</td>
              <td>{{ row.displayExpiryDate }}</td>
              <td>{{ row.displayVolume }}</td>
              <td class="font-bold">{{ row.displayQuantity }}</td>
              <td>{{ row.displayUnitPrice }}</td>
              <td class="font-bold">{{ row.displayTotalPrice }}</td>
              <td>{{ row.remark || '-' }}</td>
              <td v-if="row.isPrimaryRow" :rowspan="row.groupSize" class="print-hide bg-white border-b">
                <div class="flex items-center gap-1">
                  <button @click="editItem(row.originalIndex)" class=" editbtn hover:underline text-emerald-600">Edit</button>
                  <button @click="deleteItem(row.originalIndex)" class="delbtn p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                   Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Print Footer (Hidden unless printing) -->
        <div class="hidden print:block mt-8 w-full">
          <div class="flex flex-col gap-1.5 pl-8">
            <div class="flex items-baseline gap-2 text-sm border-t border-black pt-1 mt-1">
              <span class="font-black text-[#0d4169]">Total Amount:</span>
              <span class="font-black text-base text-[#0d4169]">{{ totalAmountFormatted }}</span>
            </div>
          </div>

          <div class="mt-16 flex justify-between px-10 text-sm">
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Issued By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Checked By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Received By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Approved By</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary + Save Button (Moved Below Table) -->
      <div class="flex justify-between items-center px-2 pt-4 print-hide">
        <div class="text-left">
          <p class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Amount</p>
          <p class="text-sm font-black text-[#0d4169]">
            {{ totalAmountFormatted }}
          </p>
        </div>
        <button @click="saveToDatabase" :disabled="isSaving"
          class="px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2">
          <div
  v-if="isSaving"
  class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
></div>
       <span v-if="!isSaving">Save SIV</span>  
        </button>
      </div>

    </div>

    <!-- Approved Requests Table (to fill form) -->
    <div v-if="!isOtherTransfer && nonTodayApprovedList.length > 0" class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 print-hide">
      <h3 class="font-bold text-sm mb-2">Approved Store Request Items</h3>
      <div class="w-full overflow-x-auto">
        <table class="w-full border-collapse bg-white">
          <thead>
            <tr>
              <th>#</th>
              <th>Req. Date</th>
              <th>Req.Department</th>
              <th>Req. name/user</th>
              <th>Req. number</th>
              <th>Category</th>
              <th>Item</th>
              <th>U.O.M</th>
              <th>Req. Qty</th>
              <th>Appr. Qty</th>
              <th>Approved By</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(a, idx) in nonTodayApprovedList" :key="a.id">
              <td>{{ idx + 1 }}</td>
              <td>{{ a.transactionDate }}</td>
              <td>{{ a.department }}</td>
              <td>{{ a.requester }}</td>
              <td>{{ a.srNumber }}</td>
              <td>{{ a.category }}</td>
              <td>{{ a.item }}</td>
              <td>{{ a.uom }}</td>
              <td>{{ a.requestedQuantity }} {{ a.uom }}</td>
              <td>{{ a.approvedQuantity }} {{ a.uom }}</td>
              <td>{{ a.approverName }}</td>
              <td><button @click="addApprovedToList(a)" class="px-2 py-1 text-blue-500 bg-blue-50 hover:bg-blue-100 rounded transition">→</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pending SIV Edit Access (requested by admin) -->
    <div v-if="pendingSivList.length > 0" class="flex flex-col gap-2 print-hide">
      <div class="w-full flex justify-between items-center mb-2 px-2">
        <h3 class="text-[0.8rem] font-bold text-slate-600">Pending SIV Edit Access Requests</h3>
      </div>
      <div class="w-full overflow-x-auto shadow-sm rounded-xl">
        <table class="w-full border-collapse bg-white overflow-hidden">
          <thead>
            <tr>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">#</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Siv No</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Date</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Department</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Category</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Item</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Unit Price</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Qty</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Volume</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider">Remark</th>
              <th class="px-2 py-1 text-left text-xs font-bold text-[#64748b] tracking-wider print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in pendingFlattenedSivList"
              :key="`pending-${index}`"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ index + 1 }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-semibold">{{ row.sivNumber }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.date }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.departmentName }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.categoryName }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold text-[#0d4169]">{{ row.itemName }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.displayUnitPrice }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700 font-bold">{{ row.displayQuantity }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.displayVolume }}</td>
              <td class="px-2 py-1.5 text-[0.7rem] text-slate-700">{{ row.remark || '-' }}</td>
              <td
                v-if="row.isPrimaryRow"
                :rowspan="row.groupSize"
                class="px-2 py-1.5 text-[0.7rem] text-slate-700 print-hide bg-white border-b"
              >
                <div class="flex items-center gap-1">
                  <button @click="loadPendingIntoForm(row.originalIndex)" class="editbtn hover:underline text-emerald-600">Edit</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="isScanModalOpen" class="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4 print-hide">
      <div class="w-full max-w-lg max-h-[85vh] overflow-hidden rounded-xl bg-white shadow-2xl">
        <div class="flex items-center justify-between bg-[#0d4169] px-5 py-3 text-white">
          <h3 class="text-base font-bold">Scan Item</h3>
          <button type="button" @click="closeScanModal" class="rounded px-2 py-1 text-lg hover:bg-white/10" aria-label="Close">×</button>
        </div>
        <div class="space-y-3 p-5">
          <div class="text-sm font-semibold text-slate-700">
            Scan qty time: {{ scanExpectedCount }}
            <span class="ml-2 text-xs font-normal text-slate-500">({{ scannedSerials.length }} scanned)</span>
          </div>
          <div class="flex gap-2">
            <input
              v-model="scanInput"
              type="text"
              autocomplete="off"
              placeholder="Scan item serial number"
              :disabled="scannedSerials.length >= scanExpectedCount || isProcessingScannedItem"
              class="siv-scan-input min-w-0 flex-1 rounded-md border border-[#cbd5e1] bg-white px-3 py-2 text-sm text-white caret-transparent outline-none focus:border-[#0d4169] placeholder:text-slate-400 disabled:bg-slate-100"
              @input="queueScannerCapture"
              @keydown="handleScannerKeydown"
              @keyup="handleScannerKeyup"
              @paste.prevent
            >
            <button
              type="button"
              class="flex h-[38px] w-10 shrink-0 items-center justify-center rounded-md border border-[#cbd5e1] bg-white text-[#0d4169] hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="scannedSerials.length >= scanExpectedCount || isProcessingScannedItem"
              aria-label="Scan barcode with camera"
              title="Scan barcode with camera"
              @click="openHostedBarcodeScanner"
            >
              <svg viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 7V5a2 2 0 0 1 2-2h2m10 0h2a2 2 0 0 1 2 2v2M3 17v2a2 2 0 0 0 2 2h2m10 0h2a2 2 0 0 0 2-2v-2M4 12h1m3-3v6m3-6v6m3-6v6m3-6v6m2-3h1" />
              </svg>
            </button>
          </div>
          <p v-if="scanFeedback" class="text-sm font-semibold" :class="scanFeedbackIsError ? 'text-red-600' : 'text-emerald-700'">
            {{ scanFeedback }}
          </p>
          <div class="max-h-60 space-y-2 overflow-y-auto rounded-md border border-slate-200 p-2">
            <div v-for="index in scanExpectedCount" :key="index" class="flex min-h-10 items-center gap-3 rounded border border-slate-100 px-3 py-2">
              <span class="w-10 shrink-0 text-sm font-bold text-slate-700">{{ index }}{{ scannedSerials[index - 1] ? '.' : ' ?' }}</span>
              <template v-if="scannedSerials[index - 1]">
                <div v-if="getCode39Barcode(scannedSerials[index - 1])" class="min-w-0 flex-1">
                  <svg
                    class="h-7 w-28 max-w-full"
                    :viewBox="`0 0 ${getCode39Barcode(scannedSerials[index - 1]).width} 32`"
                    preserveAspectRatio="xMidYMid meet"
                    role="img"
                    aria-label="Scanned serial barcode"
                  >
                    <rect
                      v-for="(bar, barIndex) in getCode39Barcode(scannedSerials[index - 1]).bars"
                      :key="barIndex"
                      :x="bar.x"
                      y="0"
                      :width="bar.width"
                      height="24"
                      fill="#111827"
                    />
                  </svg>
                </div>
                <span v-else class="min-w-0 flex-1 break-all text-xs text-slate-700">
                  <span class="block text-[0.68rem] text-amber-700">Barcode display cannot render this serial's characters.</span>
                </span>
                <button
                  type="button"
                  @click="removeScannedSerial(index - 1)"
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-600 text-sm font-bold text-white hover:bg-red-700"
                  :aria-label="`Remove scanned serial ${scannedSerials[index - 1]}`"
                  title="Remove scanned result"
                >
                  ×
                </button>
              </template>
              <span v-else class="text-sm text-slate-400">?</span>
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <button type="button" @click="closeScanModal" class="rounded-md bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300">Cancel</button>
            <button
              v-if="scannedSerials.length === scanExpectedCount"
              type="button"
              :disabled="isProcessingScannedItem"
              @click="addScannedItemToList"
              class="rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              {{ isProcessingScannedItem ? 'Adding...' : 'Add Scanned Item to List' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isReceiveApprovalScanOpen" class="fixed inset-0 z-[310] flex items-center justify-center bg-black/50 p-4 print-hide">
      <div class="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
        <div class="flex items-center justify-between bg-[#0d4169] px-5 py-3 text-white">
          <h3 class="text-base font-bold">Approve SIV by Scan</h3>
          <button type="button" @click="closeReceiveApprovalScan" class="rounded px-2 py-1 text-lg hover:bg-white/10" aria-label="Close">×</button>
        </div>
        <div class="space-y-3 p-5">
          <p class="text-sm text-slate-600">Scan the barcode for {{ receiveApprovalScanRow?.item || 'this item' }}.</p>
          <div class="text-sm font-semibold text-slate-700">
            Scan qty time: {{ receiveApprovalScanExpectedCount }}
            <span class="ml-2 text-xs font-normal text-slate-500">({{ receiveApprovalScannedSerials.length }} scanned)</span>
          </div>
          <div class="flex gap-2">
            <input
              v-model="receiveApprovalScanInput"
              type="text"
              autocomplete="off"
              placeholder="Scan item serial number"
              :disabled="receiveApprovalScannedSerials.length >= receiveApprovalScanExpectedCount || approvingReceiveIds.has(receiveApprovalScanRow?.id)"
              class="siv-approval-scan-input min-w-0 flex-1 rounded-md border border-[#cbd5e1] bg-white px-3 py-2 text-sm outline-none focus:border-[#0d4169] placeholder:text-slate-400 disabled:bg-slate-100"
              @input="queueReceiveApprovalScan"
              @keydown.enter.prevent="registerReceiveApprovalScan"
            >
            <button
              type="button"
              class="flex h-[38px] w-10 shrink-0 items-center justify-center rounded-md border border-[#cbd5e1] bg-white text-[#0d4169] hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="receiveApprovalScannedSerials.length >= receiveApprovalScanExpectedCount || approvingReceiveIds.has(receiveApprovalScanRow?.id)"
              aria-label="Scan barcode with camera"
              title="Scan barcode with camera"
              @click="openHostedBarcodeScanner"
            >
              <svg viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 7V5a2 2 0 0 1 2-2h2m10 0h2a2 2 0 0 1 2 2v2M3 17v2a2 2 0 0 0 2 2h2m10 0h2a2 2 0 0 0 2-2v-2M4 12h1m3-3v6m3-6v6m3-6v6m3-6v6m2-3h1" />
              </svg>
            </button>
          </div>
          <div class="max-h-60 space-y-2 overflow-y-auto rounded-md border border-slate-200 p-2">
            <div v-for="index in receiveApprovalScanExpectedCount" :key="index" class="flex min-h-10 items-center gap-3 rounded border border-slate-100 px-3 py-2">
              <span class="w-10 shrink-0 text-sm font-bold text-slate-700">{{ index }}{{ receiveApprovalScannedSerials[index - 1] ? '.' : ' ?' }}</span>
              <template v-if="receiveApprovalScannedSerials[index - 1]">
                <div v-if="getCode39Barcode(receiveApprovalScannedSerials[index - 1])" class="min-w-0 flex-1">
                  <svg
                    class="h-7 w-28 max-w-full"
                    :viewBox="`0 0 ${getCode39Barcode(receiveApprovalScannedSerials[index - 1]).width} 32`"
                    preserveAspectRatio="xMidYMid meet"
                    role="img"
                    aria-label="Scanned serial barcode"
                  >
                    <rect
                      v-for="(bar, barIndex) in getCode39Barcode(receiveApprovalScannedSerials[index - 1]).bars"
                      :key="barIndex"
                      :x="bar.x"
                      y="0"
                      :width="bar.width"
                      height="24"
                      fill="#111827"
                    />
                  </svg>
                </div>
                <span v-else class="min-w-0 flex-1 break-all text-xs text-slate-700">
                  <span class="block text-[0.68rem] text-amber-700">Barcode display cannot render this serial's characters.</span>
                </span>
                <button
                  type="button"
                  @click="removeReceiveApprovalScannedSerial(index - 1)"
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-600 text-sm font-bold text-white hover:bg-red-700"
                  :aria-label="`Remove scanned serial ${receiveApprovalScannedSerials[index - 1]}`"
                  title="Remove scanned result"
                >
                  ×
                </button>
              </template>
              <span v-else class="text-sm text-slate-400">?</span>
            </div>
          </div>
          <p v-if="receiveApprovalToast.show" role="alert" class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
            {{ receiveApprovalToast.message }}
          </p>
          <div class="flex justify-end gap-2">
            <button type="button" @click="closeReceiveApprovalScan" class="rounded-md bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-300">Cancel</button>
            <button
              v-if="receiveApprovalScannedSerials.length === receiveApprovalScanExpectedCount && receiveApprovalScanExpectedCount > 0"
              type="button"
              :disabled="approvingReceiveIds.has(receiveApprovalScanRow?.id)"
              @click="approveReceiveByScan"
              class="rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              {{ approvingReceiveIds.has(receiveApprovalScanRow?.id) ? 'Approving...' : 'Approve' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

// Dropdowns
const categories = ref([])
const registeredItems = ref([])
const products = ref([])
const departments = ref([])
const incomeCategories = ref([])

const selectedItemUnit = computed(() => {
  if (!form.value.selectedItemKey) return ''
  const [type, id] = form.value.selectedItemKey.split(':')
  if (type === 'product') {
    const p = products.value.find(x => x.id == id)
    return p ? (p.uom || p.unit || '') : ''
  }
  const item = registeredItems.value.find(i => i.id == id)
  return item ? item.unit : ''
})

const selectedItemIsScannable = computed(() => {
  const key = form.value.selectedItemKey
  if (!key || !key.startsWith('item:') || isOtherTransfer.value) return false
  const id = Number(key.split(':')[1])
  const item = registeredItems.value.find(candidate => Number(candidate.id) === id)
  const value = item?.is_scannable ?? item?.isScannable
  return value === true || value === 1 || value === 'true' || value === '1'
})

// State
const loading = ref(false)
const isSaving = ref(false)
const isEditing = ref(false)
const editIndex = ref(-1)
const editSource = ref(null) // 'items' | 'pending' | null
const pendingEditPermissions = ref(null)

// Stock balance
const stockBalances = ref([])
const balanceLoading = ref(false)
const isScanModalOpen = ref(false)
const isReceiveApprovalScanOpen = ref(false)
const receiveApprovalScanRow = ref(null)
const receiveApprovalScanInput = ref('')
const receiveApprovalScanExpectedCount = ref(0)
const receiveApprovalScannedSerials = ref([])
const receiveApprovalToast = ref({ show: false, message: '' })
const scanExpectedCount = ref(0)
const scanInput = ref('')
const scannedSerials = ref([])
const scanFeedback = ref('')
const scanFeedbackIsError = ref(false)
const isProcessingScannedItem = ref(false)
const scanItemKey = ref(null)
const isSyncingScanEdit = ref(false)
let scannerCaptureTimer = null
let scannerLastKeyAt = 0
let scannerSequenceConfirmed = false
let scannerInputBlocked = false
const scannerHeldKeys = new Set()
let receiveApprovalToastTimer = null
const HOSTED_BARCODE_SCANNER_ORIGIN = 'https://theminasoft.com'
let hostedBarcodeScannerWindow = null

function handleHostedBarcodeScannerMessage(event) {
  if (event.origin !== HOSTED_BARCODE_SCANNER_ORIGIN || event.source !== hostedBarcodeScannerWindow) return
  if (event.data?.type !== 'barcode-scan-result' || (!isScanModalOpen.value && !isReceiveApprovalScanOpen.value)) return
  const barcode = String(event.data.barcode || '').trim()
  hostedBarcodeScannerWindow?.close?.()
  hostedBarcodeScannerWindow = null
  if (!barcode) return
  if (isReceiveApprovalScanOpen.value) {
    receiveApprovalScanInput.value = barcode
    queueReceiveApprovalScan()
    return
  }
  scanInput.value = barcode
  scanFeedback.value = ''
  scanFeedbackIsError.value = false
  scannerSequenceConfirmed = true
  registerScannedSerial()
}

function openHostedBarcodeScanner() {
  if (!isScanModalOpen.value && !isReceiveApprovalScanOpen.value) return
  if (hostedBarcodeScannerWindow && !hostedBarcodeScannerWindow.closed) {
    hostedBarcodeScannerWindow.focus()
    return
  }
  const returnOrigin = window.location.origin
  if (!/^https?:$/.test(window.location.protocol) || !returnOrigin) {
    const message = 'The hosted barcode scanner needs this page opened over HTTP.'
    if (isReceiveApprovalScanOpen.value) showReceiveApprovalToast(message)
    else {
      scanFeedback.value = message
      scanFeedbackIsError.value = true
    }
    return
  }
  const params = new URLSearchParams({ returnOrigin })
  hostedBarcodeScannerWindow = window.open(
    `${HOSTED_BARCODE_SCANNER_ORIGIN}/barcode-scanner?${params.toString()}`,
    'hostedBarcodeScanner',
    'popup=yes,width=520,height=760,resizable=yes,scrollbars=yes',
  )
  if (!hostedBarcodeScannerWindow) {
    const message = 'Please allow the barcode scanner popup.'
    if (isReceiveApprovalScanOpen.value) showReceiveApprovalToast(message)
    else {
      scanFeedback.value = message
      scanFeedbackIsError.value = true
    }
  }
}

const code39Patterns = {
  '0': 0x034, '1': 0x121, '2': 0x061, '3': 0x160, '4': 0x031, '5': 0x130, '6': 0x070, '7': 0x025, '8': 0x124, '9': 0x064,
  A: 0x109, B: 0x049, C: 0x148, D: 0x019, E: 0x118, F: 0x058, G: 0x00d, H: 0x10c, I: 0x04c, J: 0x01c,
  K: 0x103, L: 0x043, M: 0x142, N: 0x013, O: 0x112, P: 0x052, Q: 0x007, R: 0x106, S: 0x046, T: 0x016,
  U: 0x181, V: 0x0c1, W: 0x1c0, X: 0x091, Y: 0x190, Z: 0x0d0,
  '-': 0x085, '.': 0x184, ' ': 0x0c4, '$': 0x0a8, '/': 0x0a2, '+': 0x08a, '%': 0x02a, '*': 0x094
}

function getCode39Barcode(value) {
  const serial = String(value).toUpperCase()
  if (serial.includes('*')) return null
  const encoded = `*${serial}*`
  if ([...encoded].some(character => code39Patterns[character] === undefined)) return null

  const bars = []
  let x = 0
  for (const [characterIndex, character] of [...encoded].entries()) {
    const pattern = code39Patterns[character]
    for (let bit = 8; bit >= 0; bit--) {
      const width = (pattern >> bit) & 1 ? 3 : 1
      if (bit % 2 === 0) bars.push({ x, width })
      x += width
    }
    if (characterIndex < encoded.length - 1) x += 1
  }
  return { bars, width: x }
}

function closeScanModal() {
  isScanModalOpen.value = false
  hostedBarcodeScannerWindow?.close?.()
  hostedBarcodeScannerWindow = null
  scanItemKey.value = null
  scanExpectedCount.value = 0
  scanInput.value = ''
  scannedSerials.value = []
  scanFeedback.value = ''
  scanFeedbackIsError.value = false
  scannerLastKeyAt = 0
  scannerSequenceConfirmed = false
  scannerInputBlocked = false
  scannerHeldKeys.clear()
  if (scannerCaptureTimer) {
    clearTimeout(scannerCaptureTimer)
    scannerCaptureTimer = null
  }
}

async function openScanModal() {
  if (!selectedItemIsScannable.value || (!isEditing.value && !canAddToSivList.value)) return

  const validation = await processSiv({ validateOnly: true })
  if (!validation?.valid) return

  const expectedCount = Math.round(validation.totalQuantity)
  if (!Number.isSafeInteger(expectedCount) || Math.abs(validation.totalQuantity - expectedCount) > 1e-9) {
    alert('Qty to Issue must be a whole number for scannable items.')
    return
  }

  scanItemKey.value = form.value.selectedItemKey
  scanExpectedCount.value = expectedCount
  const existingSerials = isEditing.value && editSource.value === 'items'
    ? sivList.value[editIndex.value]?.barcodeResults
    : null
  scannedSerials.value = Array.isArray(existingSerials) ? [...existingSerials] : [...scannedSerials.value]
  if (scannedSerials.value.length > expectedCount) scannedSerials.value = []
  scanInput.value = ''
  scannerLastKeyAt = 0
  scannerSequenceConfirmed = false
  scannerInputBlocked = false
  scannerHeldKeys.clear()
  scanFeedback.value = ''
  scanFeedbackIsError.value = false
  isScanModalOpen.value = true
  nextTick(() => document.querySelector('.siv-scan-input')?.focus())
}

function queueScannerCapture() {
  if (scannerCaptureTimer) clearTimeout(scannerCaptureTimer)
  scannerCaptureTimer = setTimeout(() => {
    scannerCaptureTimer = null
    if (!scanInput.value.trim()) return
    if (scannerSequenceConfirmed) {
      registerScannedSerial()
      return
    }
    scanInput.value = ''
    scannerLastKeyAt = 0
  }, 150)
}

function handleScannerKeydown(event) {
  if (scannerInputBlocked) {
    event.preventDefault()
    return
  }
  if (event.key === 'Enter') {
    event.preventDefault()
    if (scannerSequenceConfirmed) registerScannedSerial()
    return
  }
  if (event.key.length !== 1 || event.ctrlKey || event.altKey || event.metaKey) {
    event.preventDefault()
    return
  }

  if (scannerHeldKeys.size > 0 && !scannerHeldKeys.has(event.key)) {
    event.preventDefault()
    scannerInputBlocked = true
    if (scannerCaptureTimer) {
      clearTimeout(scannerCaptureTimer)
      scannerCaptureTimer = null
    }
    scanInput.value = ''
    scannerLastKeyAt = 0
    scannerSequenceConfirmed = false
    return
  }
  scannerHeldKeys.add(event.key)

  const now = Date.now()
  if (scannerLastKeyAt && now - scannerLastKeyAt > 80) {
    event.preventDefault()
    if (scannerCaptureTimer) {
      clearTimeout(scannerCaptureTimer)
      scannerCaptureTimer = null
    }
    scanInput.value = ''
    scannerLastKeyAt = 0
    scannerSequenceConfirmed = false
    return
  }

  if (scannerLastKeyAt) scannerSequenceConfirmed = true
  scannerLastKeyAt = now
}

function handleScannerKeyup(event) {
  scannerHeldKeys.delete(event.key)
  if (scannerInputBlocked && scannerHeldKeys.size === 0) {
    scannerInputBlocked = false
  }
}

function registerScannedSerial() {
  if (scannerCaptureTimer) {
    clearTimeout(scannerCaptureTimer)
    scannerCaptureTimer = null
  }
  const serial = scanInput.value.trim()
  scannerLastKeyAt = 0
  scannerSequenceConfirmed = false
  scannerInputBlocked = false
  scannerHeldKeys.clear()
  if (!serial || scannedSerials.value.length >= scanExpectedCount.value) return

  if (scannedSerials.value.some(scanned => scanned.toUpperCase() === serial.toUpperCase())) {
    scanFeedback.value = 'Already scanned. Please scan another item.'
    scanFeedbackIsError.value = true
    scanInput.value = ''
    nextTick(() => document.querySelector('.siv-scan-input')?.focus())
    return
  }

  scannedSerials.value.push(serial)
  scanInput.value = ''
  scanFeedback.value = ''
  scanFeedbackIsError.value = false
  if (scannedSerials.value.length === scanExpectedCount.value) addScannedItemToList()
  else nextTick(() => document.querySelector('.siv-scan-input')?.focus())
}

function removeScannedSerial(index) {
  const serial = scannedSerials.value[index]
  if (!serial || !confirm('Remove this scanned result?')) return
  scannedSerials.value.splice(index, 1)
  scanFeedback.value = ''
  scanFeedbackIsError.value = false
  nextTick(() => document.querySelector('.siv-scan-input')?.focus())
}

async function addScannedItemToList() {
  if (isProcessingScannedItem.value || scannedSerials.value.length !== scanExpectedCount.value) return
  isProcessingScannedItem.value = true
  try {
    const added = await processSiv()
    if (added) closeScanModal()
  } finally {
    isProcessingScannedItem.value = false
  }
}

// Display-only SIV number fetched from backend.
// This is intentionally NOT sent in the save/update payload.
const sivNumberDisplay = ref('')
const isOtherTransfer = ref(false)
const otherTransferTargetKey = ref(null)
const otherTransferBranches = ref([])
const otherTransferDepartments = ref([])
const otherTransferCompanyName = ref('')
const OTHER_TRANSFER_HEAD_OFFICE_KEY = 'company'

// Form
const getInitialForm = () => ({
  sivNo: '',
  date: new Date().toISOString().slice(0, 10),
  issuingToKey: null,
  departmentId: null,
  incomeCategoryId: null,
  categoryId: null,
  selectedItemKey: null,
  itemId: null,
  productId: null,
  remark: '',
  approvedQuantity: 0,
  approvedRequestItemId: null
})
const form = ref(getInitialForm())

// Context
const currentUser = computed(() => store.getters.currentUser)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() =>
  userPrivileges.value?.All === true || userPrivileges.value?.all === true
)
function getPrivilegeBlock(name) {
  return userPrivileges.value?.[name] || userPrivileges.value?.[String(name).toLowerCase()] || null
}
const sivPrivilege = computed(() => getPrivilegeBlock('SIV (Internal Issue)'))
const canAddToSivList = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!sivPrivilege.value?.Add
})
const canApproveSivReceive = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!sivPrivilege.value?.Approve
})
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

function parseAssignedCategoryIds(raw) {
  if (!raw) return []
  return String(raw)
    .split(',')
    .map(v => Number(String(v).trim()))
    .filter(v => Number.isInteger(v) && v > 0)
}

function filterCategoriesByUserAssignment(list) {
  const cats = Array.isArray(list) ? list : []
  const user = currentUser.value
  let privilege = {}
  const rawPriv = user?.privilege
  if (rawPriv) {
    try {
      privilege = typeof rawPriv === 'string' ? JSON.parse(rawPriv) : rawPriv
    } catch {
      privilege = {}
    }
  }
  const hasAll = privilege?.All === true || privilege?.all === true
  const assignedIds = parseAssignedCategoryIds(user?.category_ids || user?.categoryIds)

  if (hasAll && assignedIds.length === 0) {
    return cats
  }
  if (assignedIds.length === 0) {
    return cats
  }
  const idSet = new Set(assignedIds)
  return cats.filter(c => idSet.has(Number(c.id)))
}

// Select Category options: All:true + empty category_ids → all; otherwise assigned category_ids.
const selectableCategories = computed(() => filterCategoriesByUserAssignment(categories.value))

const todayDate = new Date().toISOString().split('T')[0]

function formatBalanceQty(value) {
  const n = typeof value === 'number' ? value : parseFloat(value ?? 0)
  if (!Number.isFinite(n)) return '0.000'
  return (Math.round(n * 1000) / 1000).toFixed(3)
}

function getSivLineAmount(line) {
  // Detect unit (same normalization approach used in saveToDatabase).
  let u = ''
  if (line?.productId) {
    const pInfo = products.value.find(p => p.id == line.productId)
    u = (pInfo?.uom || pInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
  } else if (line?.itemId) {
    const itemInfo = registeredItems.value.find(it => it.id == line.itemId)
    u = (itemInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
  }
  const isLiterOrKg = (
    u === 'l' ||
    u === 'liter' ||
    u === 'liters' ||
    u.startsWith('liter') ||
    u === 'kg' ||
    u === 'k.g' ||
    u === 'k.g.'
  )

  // Each temp SIV line stores comma-separated issued quantities and corresponding unit prices.
  const qStr = line?.quantityDetails
  const pStr = line?.unitPriceDetails
  const vStr = line?.variantDetails
  if (qStr && pStr) {
    const qs = String(qStr).split(',').map(s => Number(s.trim()))
    const ps = String(pStr).split(',').map(s => Number(s.trim()))
    const vs = (vStr ? String(vStr).split(',').map(s => Number(s.trim())) : [])
    const len = Math.min(qs.length, ps.length)
    let sum = 0
    for (let i = 0; i < len; i++) {
      const q = qs[i]
      const p = ps[i]
      if (!Number.isFinite(q) || !Number.isFinite(p)) continue
      if (isLiterOrKg && vs.length > 0) {
        const v = Number(vs[i] ?? vs[0] ?? 0)
        if (Number.isFinite(v) && v !== 0) {
          const baseQty = (q * 1000) / v
          sum += baseQty * p
          continue
        }
      }
      sum += q * p
    }
    return sum
  }

  // Fallback: if quantity/unitPrice are present directly.
  const q = Number(line?.quantity ?? 0)
  const p = Number(line?.unitPrice ?? 0)
  if (!Number.isFinite(q) || !Number.isFinite(p)) return 0
  return q * p
}

function getSivDisplayUnitPrice(line, unitPriceRaw, volumeRaw) {
  // Requirement: display conversion only (do not change backend payload):
  // if unit is liter or K.G. -> (price/volume)*1000
  let u = ''
  if (line?.productId) {
    const pInfo = products.value.find(p => p.id == line.productId)
    u = (pInfo?.uom || pInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
  } else if (line?.itemId) {
    const itemInfo = registeredItems.value.find(it => it.id == line.itemId)
    u = (itemInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
  }
  const isLiterOrKg = (
    u === 'l' ||
    u === 'liter' ||
    u === 'liters' ||
    u.startsWith('liter') ||
    u === 'kg' ||
    u === 'k.g' ||
    u === 'k.g.'
  )
  if (!isLiterOrKg) return unitPriceRaw

  const p = Number(unitPriceRaw)
  const v = Number(volumeRaw)
  if (!Number.isFinite(p) || !Number.isFinite(v) || v === 0) return unitPriceRaw
  return (p / v) * 1000
}

function getSivDisplayQuantity(line, quantityRaw, volumeRaw) {
  // Requirement: display conversion only (do not change backend payload):
  // if unit is liter or K.G. -> (quantity * volume)/1000
  let u = ''
  if (line?.productId) {
    const pInfo = products.value.find(p => p.id == line.productId)
    u = (pInfo?.uom || pInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
  } else if (line?.itemId) {
    const itemInfo = registeredItems.value.find(it => it.id == line.itemId)
    u = (itemInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
  }
  const isLiterOrKg = (
    u === 'l' ||
    u === 'liter' ||
    u === 'liters' ||
    u.startsWith('liter') ||
    u === 'kg' ||
    u === 'k.g' ||
    u === 'k.g.'
  )
  if (!isLiterOrKg) return quantityRaw

  const q = Number(quantityRaw)
  const v = Number(volumeRaw)
  if (!Number.isFinite(q) || !Number.isFinite(v)) return quantityRaw
  return (q * v) / 1000
}

const totalAmount = computed(() => sivList.value.reduce((sum, line) => sum + getSivLineAmount(line), 0))
const totalAmountFormatted = computed(() => {
  const n = totalAmount.value
  if (!Number.isFinite(n)) return '0.00'
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
})

// Filtered list for the dropdown - combines items and products
const filteredSelectableItems = computed(() => {
  if (!form.value.categoryId) return []
  const catId = Number(form.value.categoryId)

  const items = registeredItems.value
    .filter(item => Number(item.categoryId) === catId)
    .map(it => ({
      key: `item:${it.id}`,
      type: 'item',
      id: it.id,
      name: it.itemName,
      unit: it.unit
    }))

  const prods = products.value
    .filter(p => {
      const pCat = p.categoryId ?? p.category_id ?? p.category?.id ?? null
      return pCat == null ? true : Number(pCat) === catId
    })
    .map(p => ({
      key: `product:${p.id}`,
      type: 'product',
      id: p.id,
      name: p.name,
      unit: p.uom || p.unit || ''
    }))

  return [...items, ...prods].sort((a, b) => a.name.localeCompare(b.name))
})

// Temp list
const sivList = ref([])
const approvedList = ref([])
const pendingSivList = ref([])
const pendingReceiveApprovals = ref([])
const myPendingReceiveItems = ref([])
const myNotApprovedOtherSivItems = ref([])
const approvingReceiveIds = ref(new Set())

const todayKey = computed(() => new Date().toISOString().slice(0, 10))

function toDayKey(v) {
  if (!v) return ''
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

const todayApprovedList = computed(() => {
  const today = todayKey.value
  return (approvedList.value || []).filter(a => toDayKey(a?.approvedAt) === today)
})
const nonTodayApprovedList = computed(() => {
  const today = todayKey.value
  return (approvedList.value || []).filter(a => toDayKey(a?.approvedAt) !== today)
})

async function fetchNextSivNumberForDisplay() {
  try {
    if (!companyId.value && !branchId.value) return
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const endpoint = isOtherTransfer.value ? `/other-siv/next-number?${query}` : `/siv/next-number?${query}`
    const res = await apiFetch(endpoint)
    if (!res?.ok) return
    const data = await res.json().catch(() => ({}))
    const nextNumber = data?.nextNumber || ''
    // Display-only: do not use this value in save/update payloads.
    sivNumberDisplay.value = nextNumber
  } catch (e) {
    console.error('Error fetching SIV number for display:', e)
  }
}

function parseIssuingToSelection(key) {
  const raw = String(key || '').trim()
  if (!raw) return { departmentId: null, incomeCategoryId: null }
  if (raw.startsWith('department-')) {
    const depId = Number(raw.slice('department-'.length))
    return {
      departmentId: Number.isFinite(depId) ? depId : null,
      incomeCategoryId: null
    }
  }
  const catId = Number(raw)
  return {
    departmentId: null,
    incomeCategoryId: Number.isFinite(catId) ? catId : null
  }
}

function toIssuingToKey(departmentId, incomeCategoryId) {
  if (departmentId != null && departmentId !== '') {
    return `department-${String(departmentId)}`
  }
  if (incomeCategoryId != null && incomeCategoryId !== '') {
    return String(incomeCategoryId)
  }
  return null
}

function incomeCategoryIsActive(cat) {
  if (!cat || typeof cat !== 'object') return false
  if (cat.enabled != null) return cat.enabled === true || cat.enabled === 1 || cat.enabled === '1'
  if (cat.status != null) return Number(cat.status) === 1
  return true
}

const sivNumberLabel = computed(() =>
  isOtherTransfer.value ? 'Other Next SIV Number' : 'Next SIV Number'
)
function withOtherSivPrefix(raw, fallback = 'SIV') {
  const value = String(raw || '').trim()
  if (!isOtherTransfer.value) return value || fallback
  if (!value) return 'Other SIV'
  if (/^other(\s|-)/i.test(value)) return value
  if (/^SIV/i.test(value)) return value.replace(/^SIV/i, 'Other SIV')
  return `Other ${value}`
}

const displayedSivNumber = computed(() => {
  const raw = String(sivNumberDisplay.value || '').trim()
  if (!raw) return '-'
  return withOtherSivPrefix(raw, '-')
})

const isOtherTransferHeaderLocked = computed(() => sivList.value.length > 0)

const otherTransferBranchOptions = computed(() => {
  const loggedBranchId = branchId.value
  const options = []
  for (const branch of otherTransferBranches.value) {
    const name = String(branch?.name || '').trim()
    if (!name) continue
    if (loggedBranchId != null && loggedBranchId !== '' && String(branch.id) === String(loggedBranchId)) continue
    options.push({
      key: `branch-${String(branch.id)}`,
      label: name
    })
  }
  return options
})

function getSelectedOtherTransferBranchId() {
  const key = String(otherTransferTargetKey.value || '').trim()
  if (!key.startsWith('branch-')) return null
  const id = parseInt(key.slice('branch-'.length), 10)
  return Number.isFinite(id) && id > 0 ? id : null
}

function getSelectedOtherTransferBranchName() {
  const key = String(otherTransferTargetKey.value || '').trim()
  if (!key || key === OTHER_TRANSFER_HEAD_OFFICE_KEY) return '-'
  if (!key.startsWith('branch-')) return '-'
  const id = key.slice('branch-'.length)
  const branch = otherTransferBranches.value.find(b => String(b.id) === String(id))
  const name = String(branch?.name || '').trim()
  return name || '-'
}

function mapActiveDepartmentsToIssuingOptions(list) {
  return (Array.isArray(list) ? list : []).map((dep) => ({
    key: `department-${String(dep.id)}`,
    name: String(dep.name || '').trim()
  })).filter((opt) => opt.name)
}

const issuingToOptions = computed(() => {
  if (isOtherTransfer.value) {
    return mapActiveDepartmentsToIssuingOptions(otherTransferDepartments.value)
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  const deptOpts = mapActiveDepartmentsToIssuingOptions(departments.value)

  const catOpts = incomeCategories.value.map((cat) => ({
    key: String(cat.id),
    name: String(cat.name || '').trim()
  })).filter((opt) => opt.name)

  return [...catOpts, ...deptOpts].sort((a, b) => a.name.localeCompare(b.name))
})

// Lock Issuing To while there are temp SIV rows (department or income category).
const issuingToLockKey = ref(null)
const isIssuingToLocked = computed(() => sivList.value.length > 0 && issuingToLockKey.value != null)
const visibleIssuingToOptions = computed(() => {
  if (!isIssuingToLocked.value) return issuingToOptions.value
  return issuingToOptions.value.filter((opt) => opt.key === issuingToLockKey.value)
})

watch(
  () => form.value.issuingToKey,
  (key) => {
    const parsed = parseIssuingToSelection(key)
    form.value.departmentId = parsed.departmentId
    form.value.incomeCategoryId = parsed.incomeCategoryId
  }
)

watch(isOtherTransfer, async (checked) => {
  if (sivList.value.length > 0) return
  otherTransferTargetKey.value = null
  otherTransferDepartments.value = []
  clearOtherTransferIssuingSelection()
  if (checked) await loadOtherTransferBranches()
  await fetchNextSivNumberForDisplay()
})

watch(otherTransferTargetKey, async (key) => {
  if (!isOtherTransfer.value) return
  if (sivList.value.length > 0) return
  clearOtherTransferIssuingSelection()
  await loadOtherTransferDepartments(key)
})
const isPendingEditMode = computed(() => isEditing.value && editSource.value === 'pending')
const canEditTransactionDate = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.transactionDate)
const canEditDepartment = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.department)
const canEditCategory = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.category)
const canEditItemField = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.itemField)
const canEditRemark = computed(() => !isPendingEditMode.value || !!pendingEditPermissions.value?.remark)
const isApprovedArrowLock = computed(() => !!form.value.approvedRequestItemId && !isPendingEditMode.value)
const sivApprovalRequired = ref(false)
const isApprovalSelectionRequiredLock = computed(() =>
  !isEditing.value &&
  !isOtherTransfer.value &&
  sivApprovalRequired.value &&
  !form.value.approvedRequestItemId
)

function canEditByDetailIndex(detailFlags, fallback, idx) {
  if (!isPendingEditMode.value) return true
  if (Array.isArray(detailFlags) && detailFlags.length > 0) {
    if (detailFlags[idx] !== undefined) return !!detailFlags[idx]
    return !!detailFlags[detailFlags.length - 1]
  }
  return !!fallback
}

function canEditQtyAt(idx) {
  return canEditByDetailIndex(
    pendingEditPermissions.value?.quantityDetails,
    pendingEditPermissions.value?.qty,
    idx
  )
}

// Split list into separate rows per unit price variant for display
const flattenedSivList = computed(() => {
  const flattened = []
  sivList.value.forEach((item, originalIndex) => {
    if (item.quantityDetails && item.unitPriceDetails) {
      const qs = item.quantityDetails.split(',').map(s => s.trim())
      const ps = item.unitPriceDetails.split(',').map(s => s.trim())
      const vs = (item.variantDetails ? item.variantDetails.split(',').map(s => s.trim()) : [])
      const es = (item.expiryDateDetails ? item.expiryDateDetails.split(',').map(s => s.trim()) : [])
      
      qs.forEach((qty, i) => {
        const volumeValue = vs[i] ?? vs[0] ?? '-'
        const unitPriceConverted = getSivDisplayUnitPrice(item, ps[i], volumeValue)
        const totalPrice = Number(qty || 0) * Number(unitPriceConverted || 0)
        flattened.push({
          originalIndex,
          isPrimaryRow: i === 0,
          sivNumber: isOtherTransfer.value
            ? withOtherSivPrefix(item.sivNo || displayedSivNumber.value, displayedSivNumber.value)
            : (item.sivNo || ''),
          date: item.date,
          branchName: String(item.destinationBranchName || '').trim() || '-',
          departmentName: getIssuingToName(item.departmentId, item.incomeCategoryId, item.incomeCategoryName),
          categoryName: getCategoryName(item.categoryId),
          itemName: getSelectableName(item.productId ? 'product' : 'item', item.productId || item.itemId),
          displayVolume: volumeValue,
          displayExpiryDate: es[i] || es[0] || '-',
          displayQuantity: qty,
          displayUnitPrice: unitPriceConverted ?? (ps[i] || '-'),
          displayTotalPrice: Number.isFinite(totalPrice) ? totalPrice.toFixed(2) : '0.00',
          remark: item.remark,
          groupSize: qs.length
        })
      })
    } else {
      const v0 = item.variantDetails ? String(item.variantDetails).split(',')[0]?.trim() : null
      const e0 = item.expiryDateDetails ? String(item.expiryDateDetails).split(',')[0]?.trim() : null
      const unitPriceConverted = getSivDisplayUnitPrice(item, item.unitPrice, v0)
      const totalPrice = Number(item.quantity || 0) * Number(unitPriceConverted || 0)
      flattened.push({
        originalIndex,
        isPrimaryRow: true,
        sivNumber: isOtherTransfer.value
          ? withOtherSivPrefix(item.sivNo || displayedSivNumber.value, displayedSivNumber.value)
          : (item.sivNo || ''),
        date: item.date,
        branchName: String(item.destinationBranchName || '').trim() || '-',
        departmentName: getIssuingToName(item.departmentId, item.incomeCategoryId, item.incomeCategoryName),
        categoryName: getCategoryName(item.categoryId),
        itemName: getSelectableName(item.productId ? 'product' : 'item', item.productId || item.itemId),
        displayVolume: v0 || '-',
        displayExpiryDate: e0 || '-',
        displayQuantity: item.quantity,
        displayUnitPrice: unitPriceConverted ?? (item.unitPrice || '-'),
        displayTotalPrice: Number.isFinite(totalPrice) ? totalPrice.toFixed(2) : '0.00',
        remark: item.remark,
        groupSize: 1
      })
    }
  })
  return flattened
})

const pendingFlattenedSivList = computed(() => {
  const flattened = []
  pendingSivList.value.forEach((item, originalIndex) => {
    if (item.quantityDetails && item.unitPriceDetails) {
      const qs = item.quantityDetails.split(',').map(s => s.trim())
      const ps = item.unitPriceDetails.split(',').map(s => s.trim())
      const vs = (item.variantDetails ? item.variantDetails.split(',').map(s => s.trim()) : [])

      qs.forEach((qty, i) => {
        const unitPriceConverted = getSivDisplayUnitPrice(item, ps[i], vs[i])
        const volumeValue = vs[i] ?? vs[0] ?? '-'
        const quantityConverted = getSivDisplayQuantity(item, qty, volumeValue)
        flattened.push({
          originalIndex,
          isPrimaryRow: i === 0,
          sivNumber: isOtherTransfer.value
            ? withOtherSivPrefix(item.sivNo || displayedSivNumber.value, displayedSivNumber.value)
            : (item.sivNo || ''),
          date: item.date,
          departmentName: getIssuingToName(item.departmentId, item.incomeCategoryId, item.incomeCategoryName),
          categoryName: getCategoryName(item.categoryId),
          itemName: getSelectableName(item.productId ? 'product' : 'item', item.productId || item.itemId),
          displayQuantity: quantityConverted,
          displayUnitPrice: unitPriceConverted ?? (ps[i] || '-'),
          displayVolume: volumeValue,
          remark: item.remark,
          groupSize: qs.length || 1,
        })
      })
    } else {
      const v0 = item.variantDetails ? String(item.variantDetails).split(',')[0]?.trim() : null
      const unitPriceConverted = getSivDisplayUnitPrice(item, item.unitPrice, v0)
      const quantityConverted = getSivDisplayQuantity(item, item.quantity, v0)
      flattened.push({
        originalIndex,
        isPrimaryRow: true,
        sivNumber: isOtherTransfer.value
          ? withOtherSivPrefix(item.sivNo || displayedSivNumber.value, displayedSivNumber.value)
          : (item.sivNo || ''),
        date: item.date,
        departmentName: getIssuingToName(item.departmentId, item.incomeCategoryId, item.incomeCategoryName),
        categoryName: getCategoryName(item.categoryId),
        itemName: getSelectableName(item.productId ? 'product' : 'item', item.productId || item.itemId),
        displayQuantity: quantityConverted,
        displayUnitPrice: unitPriceConverted ?? (item.unitPrice || '-'),
        displayVolume: v0 || '-',
        remark: item.remark,
        groupSize: 1,
      })
    }
  })
  return flattened
})

function toBool(v) {
  return v === true || v === 'true' || v === 1 || v === '1'
}
function splitCsvBool(s) {
  if (s == null) return []
  return String(s)
    .split(',')
    .map(x => String(x).trim())
    .filter(x => x !== '')
    .map(v => v === 'true' || v === '1')
}
function splitCsvString(s) {
  if (s == null) return []
  return String(s).split(',').map(x => String(x).trim()).filter(x => x !== '')
}

async function loadPendingSivEditAccessRows() {
  pendingSivList.value = []
  try {
    const pendingRes = await apiFetch('/edit-access/siv?status=Pending', { method: 'GET' })
    if (!pendingRes.ok) return
    const pending = await pendingRes.json().catch(() => [])
    if (!Array.isArray(pending) || pending.length === 0) return

    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const sivRes = await apiFetch(`/siv/report?${query}`, { method: 'GET' })
    if (!sivRes.ok) return
    const sivs = await sivRes.json().catch(() => [])
    if (!Array.isArray(sivs) || sivs.length === 0) return

    const sivItemById = new Map()
    for (const s of sivs) {
      const items = Array.isArray(s?.items) ? s.items : []
      for (const si of items) {
        const id = Number(si?.id)
        if (!Number.isFinite(id)) continue
        sivItemById.set(id, { siv: s, sivItem: si })
      }
    }

    const records = []
    for (const req of pending) {
      const matched = sivItemById.get(Number(req?.sivId))
      if (!matched) continue
      const { siv, sivItem } = matched

      const sivCreatorId = Number(siv?.created_by ?? siv?.createdBy ?? siv?.creator?.id ?? 0)
      if (Number(userId.value || 0) !== sivCreatorId) continue

      const requestedItemId = Number(req?.item)
      const sivItemItemId = Number(sivItem?.itemId ?? sivItem?.item_id ?? 0)
      const sivItemProductId = Number(sivItem?.productId ?? sivItem?.product_id ?? 0)
      if (Number.isFinite(requestedItemId) && requestedItemId > 0) {
        const matchesItem = sivItemItemId > 0 && sivItemItemId === requestedItemId
        const matchesProduct = sivItemProductId > 0 && sivItemProductId === requestedItemId
        if (!matchesItem && !matchesProduct) continue
      }

      const quantityDetails = String(sivItem?.quantityDetails ?? sivItem?.quantity ?? '')
      const unitPriceDetails = String(sivItem?.unitPriceDetails ?? sivItem?.unitPrice ?? '')
      const variantDetails = String(sivItem?.variantDetails ?? '')
      const expiryDateDetails = String(sivItem?.expiryDateDetails ?? '')
      const quantities = splitCsvString(quantityDetails).map(v => Number(v) || 0)
      const totalQtyLine = quantities.reduce((sum, v) => sum + Number(v || 0), 0)
      const firstUnitPrice = Number(splitCsvString(unitPriceDetails)[0] || 0)

      const itemFromMaster = registeredItems.value.find(i => Number(i.id) === sivItemItemId)
      const categoryId = itemFromMaster?.categoryId ?? itemFromMaster?.category_id ?? null

      records.push({
        id: req?.id ?? null,
        sivItemId: Number(req?.sivId) || null,
        sivItemCreatedAt: sivItem?.created_at ?? sivItem?.createdAt ?? null,
        sivNo: siv?.sivNo || req?.sivNo || '-',
        date: siv?.date ? new Date(siv.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        departmentId: siv?.departmentId ?? siv?.department_id ?? null,
        incomeCategoryId: siv?.incomeCategoryId ?? siv?.income_category_id ?? null,
        incomeCategoryName: siv?.incomeCategory?.name ?? null,
        issuingToKey: toIssuingToKey(
          siv?.departmentId ?? siv?.department_id ?? null,
          siv?.incomeCategoryId ?? siv?.income_category_id ?? null
        ),
        categoryId,
        selectedItemKey: sivItemProductId > 0 ? `product:${sivItemProductId}` : `item:${sivItemItemId}`,
        itemId: sivItemProductId > 0 ? null : (sivItemItemId || null),
        productId: sivItemProductId > 0 ? sivItemProductId : null,
        quantity: totalQtyLine,
        unitPrice: firstUnitPrice,
        quantityDetails,
        unitPriceDetails,
        variantDetails,
        expiryDateDetails,
        remark: sivItem?.remark ?? siv?.remark ?? '',
        _permissions: {
          transactionDate: toBool(req?.transactionDate),
          department: toBool(req?.department),
          category: toBool(req?.category),
          itemField: toBool(req?.itemField),
          remark: toBool(req?.remark),
          quantityDetails: splitCsvBool(req?.quantityDetails),
          volumeDetails: splitCsvBool(req?.volumeDetails),
        }
      })
    }

    pendingSivList.value = records
  } catch (err) {
    console.error('Failed to load pending SIV edit access:', err)
  }
}

function buildPendingEditStockBalances(record, apiRows) {
  const quantities = splitCsvString(record.quantityDetails).map(Number)
  const unitPrices = splitCsvString(record.unitPriceDetails).map(Number)
  const hasVariantDetails = record.variantDetails !== undefined && record.variantDetails !== null && String(record.variantDetails).trim() !== ''
  const volumes = hasVariantDetails ? record.variantDetails.split(',').map(Number) : []
  const expiryDates = splitCsvString(record.expiryDateDetails)

  const qtyByKey = new Map()
  const lineMetaByKey = new Map()
  const lineCount = Math.max(unitPrices.length, quantities.length, 1)

  for (let i = 0; i < lineCount; i++) {
    const vol = hasVariantDetails ? (volumes[i] ?? volumes[0] ?? 0) : 0
    const expiryDate = expiryDates[i] ?? expiryDates[0] ?? ''
    const key = hasVariantDetails
      ? makeBalanceKey(unitPrices[i], vol, expiryDate)
      : makeBalanceKey(unitPrices[i], 0, expiryDate)
    if (lineMetaByKey.has(key)) continue
    const qtyForDisplay = getSivDisplayQuantity(record, quantities[i], hasVariantDetails ? vol : null)
    qtyByKey.set(key, qtyForDisplay ?? null)
    lineMetaByKey.set(key, {
      unitPrice: Number(unitPrices[i] ?? 0),
      volume: Number(vol ?? 0),
      expiryDate
    })
  }

  const apiByKey = new Map()
  for (const row of apiRows || []) {
    const key = hasVariantDetails
      ? makeBalanceKey(row.unitPrice, row.volume, row.expiryDate)
      : makeBalanceKey(row.unitPrice, 0, row.expiryDate)
    apiByKey.set(key, row)
  }

  const rows = []
  for (const [key, meta] of lineMetaByKey.entries()) {
    const apiRow = apiByKey.get(key)
    const raw = qtyByKey.get(key)
    const issueQty = raw != null ? Number(raw) : null
    if (apiRow) {
      rows.push({
        ...apiRow,
        balance: Number(apiRow.balance ?? 0),
        issueQty,
        originalIssueQty: issueQty
      })
    } else {
      rows.push({
        unitPrice: meta.unitPrice,
        volume: meta.volume,
        expiryDate: meta.expiryDate,
        balance: 0,
        issueQty,
        originalIssueQty: issueQty
      })
    }
  }

  return sortBalanceRowsByExpiry(rows)
}

function loadPendingIntoForm(index) {
  const record = pendingSivList.value[index]
  if (!record) return
  suppressBalanceWatch.value = true
  isEditing.value = true
  editIndex.value = index
  editSource.value = 'pending'
  pendingEditPermissions.value = record?._permissions || null
  form.value = { ...form.value, ...record }

  const isProduct = !!record.productId
  const id = isProduct ? record.productId : record.itemId

  balanceLoading.value = true
  const endpoint = isProduct ? `/products/${id}/stock-balance-with-expiry` : `/items/${id}/stock-balance-with-expiry`
  apiFetch(endpoint)
    .then(res => res.json())
    .then(data => {
      stockBalances.value = buildPendingEditStockBalances(record, data.data || [])
    })
    .catch(err => {
      console.error('Failed to load pending balance during edit:', err)
      stockBalances.value = buildPendingEditStockBalances(record, [])
    })
    .finally(() => { balanceLoading.value = false; suppressBalanceWatch.value = false })
}

// Control whether itemId watcher should auto-fetch balances
const suppressBalanceWatch = ref(false)

function makeBalanceKey(unitPrice, volume, expiryDate) {
  const norm = (n) => Math.round(Number(n ?? 0) * 10000) / 10000
  const exp = String(expiryDate || '').trim()
  return `${norm(unitPrice)}|${norm(volume)}|${exp}`
}

function sortBalanceRowsByExpiry(rows) {
  const toDateMs = (value) => {
    const raw = String(value || '').trim()
    if (!raw) return Number.POSITIVE_INFINITY
    const ms = new Date(raw).getTime()
    return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms
  }

  return [...rows].sort((a, b) => {
    const byExpiry = toDateMs(a?.expiryDate) - toDateMs(b?.expiryDate)
    if (byExpiry !== 0) return byExpiry
    const byPrice = Number(a?.unitPrice || 0) - Number(b?.unitPrice || 0)
    if (byPrice !== 0) return byPrice
    return Number(a?.volume || 0) - Number(b?.volume || 0)
  })
}

function getReservedQtyByPriceVolume(type, selectedId) {
  const reserved = new Map()
  const isSameTarget = (line) => {
    if (type === 'product') return Number(line?.productId) === Number(selectedId)
    return Number(line?.itemId) === Number(selectedId)
  }

  for (const line of sivList.value) {
    if (!isSameTarget(line)) continue
    const quantities = String(line?.quantityDetails ?? '')
      .split(',')
      .map(s => Number(String(s).trim()))
      .filter(q => Number.isFinite(q) && q > 0)
    const prices = String(line?.unitPriceDetails ?? '')
      .split(',')
      .map(s => Number(String(s).trim()))
    const volumes = String(line?.variantDetails ?? '')
      .split(',')
      .map(s => Number(String(s).trim()))
    const expiries = String(line?.expiryDateDetails ?? '')
      .split(',')
      .map(s => String(s).trim())

    const len = Math.min(quantities.length, prices.length)
    for (let i = 0; i < len; i++) {
      const qty = Number(quantities[i] || 0)
      const price = Number(prices[i])
      const volume = Number(volumes[i] ?? 0)
      const expiryDate = expiries[i] ?? expiries[0] ?? ''
      if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price)) continue
      const key = makeBalanceKey(price, volume, expiryDate)
      reserved.set(key, (reserved.get(key) || 0) + qty)
    }
  }

  return reserved
}

// Watch item selection to load stock balance
watch(() => form.value.selectedItemKey, async (newKey) => {
  if (isScanModalOpen.value && newKey !== scanItemKey.value && !isProcessingScannedItem.value) closeScanModal()
  if (suppressBalanceWatch.value) return
  stockBalances.value = []
  
  // Update internal IDs
  if (!newKey) {
    form.value.itemId = null
    form.value.productId = null
    return
  }

  const [type, idStr] = newKey.split(':')
  const id = Number(idStr)
  form.value.itemId = type === 'item' ? id : null
  form.value.productId = type === 'product' ? id : null

  balanceLoading.value = true
  try {
    const endpoint = type === 'product' ? `/products/${id}/stock-balance-with-expiry` : `/items/${id}/stock-balance-with-expiry`
    const res = await apiFetch(endpoint)
    if (res.ok) {
      const data = await res.json()
      const reservedQtyByKey = getReservedQtyByPriceVolume(type, id)

      // Filter out zero balance batches for new selections
      stockBalances.value = sortBalanceRowsByExpiry((data.data || [])
        .map(row => {
          const key = makeBalanceKey(row.unitPrice, row.volume, row.expiryDate)
          const reservedQty = Number(reservedQtyByKey.get(key) || 0)
          const currentBalance = Number(row.balance || 0)
          const adjustedBalance = Math.max(0, currentBalance - reservedQty)
          return { ...row, balance: adjustedBalance, issueQty: null }
        })

        .filter(row => Number(row.balance) > 0))
    }
  } catch (err) { console.error('Failed to load stock balance:', err) }
  finally { balanceLoading.value = false }
})

watch(isOtherTransfer, (enabled) => {
  if (enabled && isScanModalOpen.value && !isProcessingScannedItem.value) closeScanModal()
})

watch(
  () => JSON.stringify({
    categoryId: form.value.categoryId,
    selectedItemKey: form.value.selectedItemKey,
    issuingToKey: form.value.issuingToKey,
    departmentId: form.value.departmentId,
    incomeCategoryId: form.value.incomeCategoryId,
    balanceVariants: stockBalances.value.map(({ unitPrice, volume, expiryDate }) => ({
      unitPrice,
      volume,
      expiryDate
    }))
  }),
  () => {
    if (isSyncingScanEdit.value || !scannedSerials.value.length) return
    scannedSerials.value = []
    scanFeedback.value = ''
    scanFeedbackIsError.value = false
  },
  { flush: 'sync' }
)

watch(
  () => stockBalances.value.reduce((sum, row) => sum + Math.max(0, Number(row.issueQty) || 0), 0),
  (quantity) => {
    if (isSyncingScanEdit.value) return
    if (scannedSerials.value.length > quantity) {
      scannedSerials.value = []
      scanFeedback.value = ''
      scanFeedbackIsError.value = false
    }
    if (isScanModalOpen.value) {
      scanExpectedCount.value = Number.isSafeInteger(quantity) ? quantity : Math.max(0, Math.ceil(quantity))
    }
  },
  { flush: 'sync' }
)

// Fetch
async function loadData() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const [catRes, itemRes, depRes, prodRes, incomeCatRes] = await Promise.all([
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`),
      apiFetch(`/departments?${query}`),
      apiFetch(`/products?${query}&activeOnly=true`),
      apiFetch('/finance/income-category')
    ])
    if (catRes.ok) categories.value = await catRes.json()
    categories.value = categories.value.filter(c => c.status === 1);
  categories.value.sort((a, b) => a.name.localeCompare(b.name))
    if (itemRes.ok) {
      const itemData = await itemRes.json()
      registeredItems.value = Array.isArray(itemData) ? itemData.filter(i => Number(i?.status) === 1) : []
    }
    if (depRes.ok) departments.value = await depRes.json()
    departments.value = departments.value.filter(d => d.status === 1);
    departments.value.sort((a, b) => a.name.localeCompare(b.name))

    if (prodRes.ok) {
      const productData = await prodRes.json()
      products.value = Array.isArray(productData) ? productData.filter(p => Number(p?.status) === 1) : []
    }
    if (incomeCatRes.ok) {
      const incomeCatData = await incomeCatRes.json().catch(() => [])
      incomeCategories.value = Array.isArray(incomeCatData)
        ? incomeCatData.filter(incomeCategoryIsActive)
        : []
      incomeCategories.value.sort((a, b) => String(a.name).localeCompare(String(b.name)))
    } else {
      incomeCategories.value = []
    }
  } catch (err) { console.error(err) }
  finally { loading.value = false }
}

function branchIsActive(row) {
  if (!row || typeof row !== 'object') return false
  const status = row.is_active ?? row.isActive ?? row.status
  return status === 1 || status === '1' || status === true
}

function departmentIsActive(row) {
  if (!row || typeof row !== 'object') return false
  return Number(row.status ?? 1) === 1
}

function clearOtherTransferIssuingSelection() {
  form.value.issuingToKey = null
  form.value.departmentId = null
  form.value.incomeCategoryId = null
}

async function loadOtherTransferBranches() {
  try {
    const res = await apiFetch('/branches/monthly-configuration/active')
    if (!res.ok) {
      otherTransferBranches.value = []
      otherTransferCompanyName.value = ''
      return
    }
    const data = await res.json().catch(() => ({}))
    otherTransferCompanyName.value = String(data?.company?.name || '').trim()
    const list = Array.isArray(data?.branches) ? data.branches : []
    otherTransferBranches.value = list
      .filter(branchIsActive)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  } catch (err) {
    console.error('Error loading other-transfer branches:', err)
    otherTransferBranches.value = []
    otherTransferCompanyName.value = ''
  }
}

async function loadOtherTransferDepartments(targetKey) {
  const key = String(targetKey || '').trim()
  if (!key) {
    otherTransferDepartments.value = []
    return
  }

  try {
    const endpoint = key === OTHER_TRANSFER_HEAD_OFFICE_KEY
      ? '/departments/monthly-configuration/company'
      : (key.startsWith('branch-')
        ? `/departments/monthly-configuration/branch/${key.slice('branch-'.length)}`
        : '')
    if (!endpoint) {
      otherTransferDepartments.value = []
      return
    }

    const res = await apiFetch(endpoint)
    if (!res.ok) {
      otherTransferDepartments.value = []
      return
    }
    const data = await res.json().catch(() => [])
    otherTransferDepartments.value = (Array.isArray(data) ? data : [])
      .filter(departmentIsActive)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  } catch (err) {
    console.error('Error loading other-transfer departments:', err)
    otherTransferDepartments.value = []
  }
}

async function loadApprovedItems() {
  try {
    const baseQuery = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`

    // only show approved items for the last 1 month
    const end = new Date()
    const start = new Date()
    start.setMonth(start.getMonth() - 1)
    const startDate = start.toISOString().slice(0, 10)
    const endDate = end.toISOString().slice(0, 10)
    const query = `${baseQuery}&startDate=${startDate}&endDate=${endDate}`

    const res = await apiFetch(`/store-request/approved?${query}`)
    if (res.ok) {
      const data = await res.json()
      // Approved Store Request Items: items only (exclude product lines).
      const rows = Array.isArray(data.data) ? data.data : []
      approvedList.value = rows.filter((row) => {
        const itemId = Number(row?.itemId ?? row?.item_id ?? null)
        const productId = Number(row?.productId ?? row?.product_id ?? null)
        const isProduct = String(row?.itemType || '').toLowerCase() === 'product'
        if (isProduct) return false
        if (Number.isFinite(productId) && productId > 0 && !(Number.isFinite(itemId) && itemId > 0)) return false
        return Number.isFinite(itemId) && itemId > 0
      })
    }
  } catch (err) { console.error('Failed to load approved items', err) }
}

async function loadPendingReceiveApprovals() {
  try {
    // Original pending approvals list (department-scoped by backend token context).
    const res = await apiFetch('/siv/receive/pending')
    if (!res.ok) {
      pendingReceiveApprovals.value = []
      return
    }
    const data = await res.json().catch(() => ({}))
    pendingReceiveApprovals.value = (Array.isArray(data?.data) ? data.data : []).map(mapReceiveListRow)
  } catch (err) {
    console.error('Failed to load pending receive approvals', err)
    pendingReceiveApprovals.value = []
  }
}

async function loadPendingReceiveItems() {
  try {
    // Backend resolves current logged-in user from auth token and returns
    // only items where receiveApproved = 0 and parent SIV created_by is that user.
    const res = await apiFetch('/siv/receive/pending/mine')
    if (!res.ok) {
      myPendingReceiveItems.value = []
      return
    }
    const data = await res.json().catch(() => ({}))
    myPendingReceiveItems.value = (Array.isArray(data?.data) ? data.data : []).map(mapReceiveListRow)
  } catch (err) {
    console.error('Failed to load pending receive SIV items', err)
    myPendingReceiveItems.value = []
  }
}

async function loadNotApprovedOtherSivItems() {
  try {
    // Backend resolves current logged-in user from auth token and returns
    // Other SIV items where parent other_sivs.status = 0 and created_by is that user.
    const res = await apiFetch('/other-siv/not-approved')
    if (!res.ok) {
      myNotApprovedOtherSivItems.value = []
      return
    }
    const data = await res.json().catch(() => ({}))
    myNotApprovedOtherSivItems.value = (Array.isArray(data?.data) ? data.data : []).map(mapReceiveListRow)
  } catch (err) {
    console.error('Failed to load not approved Other SIV items', err)
    myNotApprovedOtherSivItems.value = []
  }
}

function showReceiveApprovalToast(message) {
  receiveApprovalToast.value = { show: true, message }
  if (receiveApprovalToastTimer) clearTimeout(receiveApprovalToastTimer)
  receiveApprovalToastTimer = setTimeout(() => {
    receiveApprovalToast.value.show = false
    receiveApprovalToastTimer = null
  }, 15000)
}

function openReceiveApprovalScan(row) {
  if (!canApproveSivReceive.value || approvingReceiveIds.value.has(row?.id)) return
  if (receiveApprovalToastTimer) clearTimeout(receiveApprovalToastTimer)
  receiveApprovalToast.value = { show: false, message: '' }
  receiveApprovalScanRow.value = row
  receiveApprovalScanInput.value = ''
  receiveApprovalScannedSerials.value = []
  const quantity = Number(row?.qty)
  const roundedQuantity = Math.round(quantity)
  const quantityIsValid = Number.isSafeInteger(roundedQuantity)
    && roundedQuantity > 0
    && Math.abs(quantity - roundedQuantity) <= 1e-9
  receiveApprovalScanExpectedCount.value = quantityIsValid ? roundedQuantity : 0
  isReceiveApprovalScanOpen.value = true
  if (!quantityIsValid) {
    showReceiveApprovalToast('Qty to Receive must be a positive whole number for barcode approval.')
    return
  }
  nextTick(() => document.querySelector('.siv-approval-scan-input')?.focus())
}

function closeReceiveApprovalScan() {
  isReceiveApprovalScanOpen.value = false
  receiveApprovalScanRow.value = null
  receiveApprovalScanInput.value = ''
  receiveApprovalScanExpectedCount.value = 0
  receiveApprovalScannedSerials.value = []
  receiveApprovalToast.value = { show: false, message: '' }
  if (receiveApprovalToastTimer) {
    clearTimeout(receiveApprovalToastTimer)
    receiveApprovalToastTimer = null
  }
  if (scannerCaptureTimer) {
    clearTimeout(scannerCaptureTimer)
    scannerCaptureTimer = null
  }
  hostedBarcodeScannerWindow?.close?.()
  hostedBarcodeScannerWindow = null
}

function queueReceiveApprovalScan() {
  if (scannerCaptureTimer) clearTimeout(scannerCaptureTimer)
  scannerCaptureTimer = setTimeout(() => {
    scannerCaptureTimer = null
    if (receiveApprovalScanInput.value.trim()) registerReceiveApprovalScan()
  }, 150)
}

async function registerReceiveApprovalScan() {
  if (scannerCaptureTimer) {
    clearTimeout(scannerCaptureTimer)
    scannerCaptureTimer = null
  }
  const row = receiveApprovalScanRow.value
  const serial = receiveApprovalScanInput.value.trim()
  if (!row || !serial || receiveApprovalScannedSerials.value.length >= receiveApprovalScanExpectedCount.value || approvingReceiveIds.value.has(row.id)) return

  const barcodeResult = (Array.isArray(row.barcodeResults) ? row.barcodeResults : [])
    .find(result => String(result?.serialNumber || '').trim().toUpperCase() === serial.toUpperCase())
  if (!barcodeResult) {
    receiveApprovalScanInput.value = ''
    showReceiveApprovalToast('Scanned barcode is not in this SIV and receiving department.')
    nextTick(() => document.querySelector('.siv-approval-scan-input')?.focus())
    return
  }
  if (receiveApprovalScannedSerials.value.some(scanned => scanned.toUpperCase() === serial.toUpperCase())) {
    receiveApprovalScanInput.value = ''
    showReceiveApprovalToast('Already scanned. Please scan another item.')
    nextTick(() => document.querySelector('.siv-approval-scan-input')?.focus())
    return
  }
  const selectedItemId = Number(row.itemId)
  const scannedItemId = Number(barcodeResult.itemId)
  if (!Number.isSafeInteger(selectedItemId) || selectedItemId <= 0 || scannedItemId !== selectedItemId) {
    receiveApprovalScanInput.value = ''
    showReceiveApprovalToast('Scanned serial belongs to a different item than the selected SIV row.')
    nextTick(() => document.querySelector('.siv-approval-scan-input')?.focus())
    return
  }

  receiveApprovalScannedSerials.value.push(serial)
  receiveApprovalScanInput.value = ''
  if (receiveApprovalToastTimer) {
    clearTimeout(receiveApprovalToastTimer)
    receiveApprovalToastTimer = null
  }
  receiveApprovalToast.value = { show: false, message: '' }
  if (receiveApprovalScannedSerials.value.length === receiveApprovalScanExpectedCount.value) {
    await approveReceiveByScan()
  } else {
    nextTick(() => document.querySelector('.siv-approval-scan-input')?.focus())
  }
}

async function approveReceiveByScan() {
  const row = receiveApprovalScanRow.value
  if (!row || receiveApprovalScannedSerials.value.length !== receiveApprovalScanExpectedCount.value || approvingReceiveIds.value.has(row.id)) return
  const approved = await approveReceive(row.id, { confirmBeforeApprove: false, useToast: true })
  if (approved) closeReceiveApprovalScan()
}

function removeReceiveApprovalScannedSerial(index) {
  const serial = receiveApprovalScannedSerials.value[index]
  if (!serial || !confirm('Remove this scanned result?')) return
  receiveApprovalScannedSerials.value.splice(index, 1)
}

async function approveReceive(id, { confirmBeforeApprove = true, useToast = false } = {}) {
  if (!canApproveSivReceive.value) return false
  const itemId = Number(id)
  if (!Number.isFinite(itemId)) return false
  if (approvingReceiveIds.value.has(itemId)) return false
  if (confirmBeforeApprove && !confirm('Are you sure ......................... Approve?')) return false

  const next = new Set(approvingReceiveIds.value)
  next.add(itemId)
  approvingReceiveIds.value = next

  try {
    const res = await apiFetch(`/siv/receive/${itemId}/approve`, { method: 'POST' })
    if (res.ok) {
      await loadPendingReceiveApprovals()
      await loadPendingReceiveItems()
      return true
    } else {
      const err = await res.json().catch(() => ({}))
      const message = err?.message || 'Failed to approve'
      if (useToast) showReceiveApprovalToast(message)
      else alert(message)
    }
  } catch (err) {
    console.error('Failed to approve receive', err)
    if (useToast) showReceiveApprovalToast(err?.message || 'Failed to approve')
  } finally {
    const after = new Set(approvingReceiveIds.value)
    after.delete(itemId)
    approvingReceiveIds.value = after
  }
  return false
}

async function isSivApprovalRequired() {
  try {
    // Backend resolves settings by authenticated user's company/branch context.
    const res = await apiFetch('/settings')
    if (!res.ok) return false
    const data = await res.json()
    return Number(data?.siv_by_approval || 0) === 1
  } catch (err) {
    console.error('Failed to read SIV approval setting:', err)
    return false
  }
}

async function refreshSivApprovalRequiredSetting() {
  sivApprovalRequired.value = await isSivApprovalRequired()
}

// Add to SIV list - aggregate quantities and unit prices into comma-separated details
async function processSiv({ validateOnly = false } = {}) {
  if (!isEditing.value && !canAddToSivList.value) return
  const approvalRequired = !isEditing.value && !isOtherTransfer.value && await isSivApprovalRequired()
  if (approvalRequired && !form.value.approvedRequestItemId) {
    alert('You cannot create siv without permission, please contact your admin.')
    return
  }

  if (!isEditing.value || editSource.value !== 'pending') {
    if (isOtherTransfer.value) {
      if (!String(otherTransferTargetKey.value || '').trim()) {
        alert('Please select a branch.')
        return
      }
      // if (form.value.issuingToKey == null || form.value.issuingToKey === '') {
      //   alert('Please select a department.')
      //   return
      // }
    } else if (
      form.value.issuingToKey == null ||
      form.value.issuingToKey === ''
    ) {
      alert('Please select a department.')
      return
    }
  }

  const isPendingSivUpdate = isEditing.value && editSource.value === 'pending'

  let rowsToIssue
  if (isPendingSivUpdate) {
    if (stockBalances.value.length === 0) {
      alert('Please enter at least one Qty to Issue in the Current Balance table.')
      return
    }
    // Pending edit access: include all SIV lines and allow qty 0 (e.g. zero-out a row).
    rowsToIssue = stockBalances.value.map(row => ({
      ...row,
      issueQty: Number(row.issueQty ?? 0)
    }))
  } else {
    rowsToIssue = stockBalances.value.filter(row => Number(row.issueQty) > 0)
    if (rowsToIssue.length === 0) {
      alert('Please enter at least one Qty to Issue in the Current Balance table.')
      return
    }
  }

  // 1. Validate stock constraints
  if (isEditing.value && editSource.value === 'pending') {
    // Pending edit rule: validate only increase difference against balance.
    for (const row of stockBalances.value) {
      const enteredQty = Number(row?.issueQty || 0)
      const originalQty = Number(row?.originalIssueQty || 0)
      const balance = Number(row?.balance || 0)
      const increasedBy = Math.max(0, enteredQty - originalQty)
      if (increasedBy > balance) {
        alert(`Cannot increase by ${increasedBy}. Only ${balance} available for unit price ${row.unitPrice} and volume ${row.volume}.`)
        return
      }
    }
  } else {
    // Normal/new SIV behavior stays unchanged.
    for (const row of rowsToIssue) {
      if (Number(row.issueQty) > row.balance) {
        alert(`Cannot issue ${row.issueQty}. Only ${row.balance} available for unit price ${row.unitPrice} and volume ${row.volume}.`)
        return
      }
    }
  }

  const quantities = rowsToIssue.map(row => Number(row.issueQty))
  const unitPrices = rowsToIssue.map(row => row.unitPrice)
  const volumes = rowsToIssue.map(row => row.volume)
  const expiryDates = rowsToIssue.map(row => String(row.expiryDate || '').trim())
  const totalQuantity = quantities.reduce((sum, q) => sum + q, 0)

  // 2. Validate total against Approved Quantity if from approval
  if (form.value.approvedRequestItemId && totalQuantity > form.value.approvedQuantity) {
    alert(`Total issue quantity (${totalQuantity}) exceeds approved quantity (${form.value.approvedQuantity}).`)
    return
  }
  if (validateOnly) return { valid: true, totalQuantity }

  const entry = {
    ...form.value,
    sivNo: isOtherTransfer.value ? displayedSivNumber.value : (form.value.sivNo || ''),
    destinationBranchId: isOtherTransfer.value ? getSelectedOtherTransferBranchId() : null,
    destinationBranchName: isOtherTransfer.value ? getSelectedOtherTransferBranchName() : null,
    quantity: totalQuantity,
    unitPrice: null,
    // In local temp list, store the actual Qty to Issue per row.
    // Conversion to base quantity (qty * 1000 / volume) is done only when saving to DB.
    quantityDetails: quantities.join(','),
    unitPriceDetails: unitPrices.join(','),
    variantDetails: volumes.join(','),
    expiryDateDetails: expiryDates.join(','),
    barcodeResults: selectedItemIsScannable.value
      ? [...scannedSerials.value]
      : (isEditing.value ? [...(sivList.value[editIndex.value]?.barcodeResults || [])] : []),
    itemType: form.value.productId ? 'product' : 'item'
  }

  if (isOtherTransfer.value) {
    entry.approvedRequestItemId = null
    entry.approvedQuantity = 0
  }

  if (isEditing.value) {
    if (editSource.value === 'pending') {
      const currentPending = pendingSivList.value[editIndex.value]
      if (!currentPending) {
        alert('Pending SIV record not found.')
        return
      }

      try {
        const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
        const grvRes = await apiFetch(`/grv/report?${query}`, { method: 'GET' })
        if (grvRes.ok) {
          const grvs = await grvRes.json().catch(() => [])
          if (Array.isArray(grvs) && grvs.length > 0) {
            const targetItemId = Number(currentPending.itemId || entry.itemId || 0)
            const targetProductId = Number(currentPending.productId || entry.productId || 0)

            let latestGrvTransactionDate = null
            for (const g of grvs) {
              const items = Array.isArray(g?.items) ? g.items : []
              for (const gi of items) {
                const giItemId = Number(gi?.itemId ?? gi?.item_id ?? 0)
                const giProductId = Number(gi?.productId ?? gi?.product_id ?? 0)
                const sameItem = targetItemId > 0 && giItemId === targetItemId
                const sameProduct = targetProductId > 0 && giProductId === targetProductId
                if (!sameItem && !sameProduct) continue
                const grvDateRaw = g?.date ?? null
                const grvDate = grvDateRaw ? new Date(grvDateRaw) : null
                if (!grvDate || Number.isNaN(grvDate.getTime())) continue
                if (!latestGrvTransactionDate || grvDate > latestGrvTransactionDate) latestGrvTransactionDate = grvDate
              }
            }

            const sivTransactionDateRaw = entry.date ?? currentPending.date ?? null
            const sivTransactionDate = sivTransactionDateRaw ? new Date(sivTransactionDateRaw) : null
            if (
              latestGrvTransactionDate &&
              sivTransactionDate &&
              !Number.isNaN(sivTransactionDate.getTime()) &&
              sivTransactionDate < latestGrvTransactionDate
            ) {
              alert('You cannot edit this siv item, its transaction date must be greater than or equal to the latest grv transaction date')
              return
            }
          }
        }
      } catch (err) {
        console.error('Failed latest GRV check for pending SIV update:', err)
      }

      const completeRes = await apiFetch(`/edit-access/siv/${currentPending.id}/complete`, {
        // Match saveToDatabase conversion behavior for liter/K.G. items.
        // For these units: quantityDetails -> base qty via (qty * 1000) / volume.
        // This keeps pending update persistence consistent with normal Save SIV.
        method: 'PATCH',
        body: JSON.stringify((() => {
          let u = ''
          if (entry.productId) {
            const pInfo = products.value.find(p => p.id == entry.productId)
            u = (pInfo?.uom || pInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
          } else {
            const itemInfo = registeredItems.value.find(it => it.id == entry.itemId)
            u = (itemInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
          }
          const isLiterOrKg = (
            u === 'l' ||
            u === 'liter' ||
            u === 'liters' ||
            u.startsWith('liter') ||
            u === 'kg' ||
            u === 'k.g' ||
            u === 'k.g.'
          )

          let quantityDetailsForApi = entry.quantityDetails
          if (isLiterOrKg && entry.quantityDetails && entry.variantDetails) {
            const qtyArr = entry.quantityDetails.split(',').map(Number)
            const volArr = entry.variantDetails.split(',').map(Number)
            const baseQtys = qtyArr.map((q, idx) => {
              const v = Number(volArr[idx] ?? volArr[0] ?? 0)
              if (!Number.isFinite(v) || v === 0) return q
              return (q * 1000) / v
            })
            quantityDetailsForApi = baseQtys.join(',')
          }

          return {
            sivItemId: currentPending.sivItemId,
            update: {
              transactionDate: entry.date,
              departmentId: entry.departmentId,
              incomeCategoryId: entry.incomeCategoryId,
              itemType: entry.itemType,
              itemId: entry.itemId,
              productId: entry.productId,
              quantityDetails: quantityDetailsForApi,
              unitPriceDetails: entry.unitPriceDetails,
              variantDetails: entry.variantDetails,
              expiryDateDetails: entry.expiryDateDetails,
              remark: entry.remark
            }
          }
        })())
      })
      if (!completeRes.ok) {
        const errData = await completeRes.json().catch(() => ({}))
        alert(errData?.message || 'Failed to update pending SIV.')
        return
      }
      await loadPendingSivEditAccessRows()
      alert('Pending SIV updated successfully.')
    } else {
      sivList.value[editIndex.value] = entry
    }
    isEditing.value = false
    editIndex.value = -1
    editSource.value = null
    pendingEditPermissions.value = null
  } else {
    sivList.value.unshift(entry)
    let addedApprovedId = entry.approvedRequestItemId || null
    if (addedApprovedId) {
      approvedList.value = approvedList.value.filter(a => a.id !== addedApprovedId)
    }
  }

  // Lock the header issuing target to the first temp row's department or income category.
  if (issuingToLockKey.value == null) {
    const lockKey = toIssuingToKey(entry.departmentId, entry.incomeCategoryId)
    if (lockKey) issuingToLockKey.value = lockKey
  }
  if (!isEditing.value || editSource.value !== 'pending') localSave()

  // Reset form
  const currentHeader = {
    date: form.value.date,
    issuingToKey: form.value.issuingToKey,
    departmentId: form.value.departmentId,
    incomeCategoryId: form.value.incomeCategoryId
  }
  form.value = { ...getInitialForm(), ...currentHeader }
  stockBalances.value = []
  return true
}

function editItem(index) {
  const item = sivList.value[index]
  isSyncingScanEdit.value = true
  suppressBalanceWatch.value = true
  form.value = {
    ...item,
    issuingToKey: toIssuingToKey(item.departmentId, item.incomeCategoryId)
  }
  isEditing.value = true
  editIndex.value = index
  editSource.value = 'items'
  pendingEditPermissions.value = null
  scanItemKey.value = selectedItemIsScannable.value ? form.value.selectedItemKey : null
  scannedSerials.value = selectedItemIsScannable.value && Array.isArray(item.barcodeResults)
    ? [...item.barcodeResults]
    : []

  const isProduct = !!item.productId
  const id = isProduct ? item.productId : item.itemId

  // Manually trigger balance fetch and pre-fill quantities
  const quantities = item.quantityDetails ? item.quantityDetails.split(',').map(Number) : [item.quantity]
  const unitPrices = item.unitPriceDetails ? item.unitPriceDetails.split(',').map(Number) : [item.unitPrice]
  const hasVariantDetails = item.variantDetails !== undefined && item.variantDetails !== null && String(item.variantDetails).trim() !== ''
  const volumes = hasVariantDetails ? item.variantDetails.split(',').map(Number) : []
  const expiryDates = item.expiryDateDetails ? String(item.expiryDateDetails).split(',').map(s => String(s).trim()) : []
  const issueQtys = quantities

  balanceLoading.value = true
  const endpoint = isProduct ? `/products/${id}/stock-balance-with-expiry` : `/items/${id}/stock-balance-with-expiry`

  apiFetch(endpoint)
    .then(res => res.json())
    .then(data => {
      const qtyByKey = new Map()
      let qtyByUnitPrice = null
      if (hasVariantDetails) {
        for (let i = 0; i < unitPrices.length; i++) {
          const vol = volumes[i] ?? volumes[0] ?? null
          const expiryDate = expiryDates[i] ?? expiryDates[0] ?? ''
          qtyByKey.set(makeBalanceKey(unitPrices[i], vol, expiryDate), issueQtys[i] ?? null)
        }
      } else {
        qtyByUnitPrice = new Map()
        for (let i = 0; i < unitPrices.length; i++) {
          const expiryDate = expiryDates[i] ?? expiryDates[0] ?? ''
          const key = makeBalanceKey(unitPrices[i], 0, expiryDate)
          if (!qtyByUnitPrice.has(key)) qtyByUnitPrice.set(key, issueQtys[i] ?? null)
        }
      }

      stockBalances.value = sortBalanceRowsByExpiry((data.data || [])
        .filter(row => {
          // During edit, always show rows that have an existing issue quantity,
          // even if the available balance is 0 (to allow the user to see what they added).
          const key = hasVariantDetails
            ? makeBalanceKey(row.unitPrice, row.volume, row.expiryDate)
            : makeBalanceKey(row.unitPrice, 0, row.expiryDate)
          const hasExistingQty = hasVariantDetails ? qtyByKey.has(key) : qtyByUnitPrice?.has(key)
          return Number(row.balance) > 0 || hasExistingQty
        })
        .map(row => {
          if (hasVariantDetails) {
            const key = makeBalanceKey(row.unitPrice, row.volume, row.expiryDate)
            const raw = qtyByKey.has(key) ? qtyByKey.get(key) : null
            const b = Number(row.balance ?? 0)
            const issueQty = raw != null ? Math.min(Number(raw), b) : null
            return { ...row, issueQty }
          }
          const unitPriceKey = makeBalanceKey(row.unitPrice, 0, row.expiryDate)
          const raw = qtyByUnitPrice?.has(unitPriceKey) ? qtyByUnitPrice.get(unitPriceKey) : null
          const b = Number(row.balance ?? 0)
          const issueQty = raw != null ? Math.min(Number(raw), b) : null
          return { ...row, issueQty }
        }))
    })
    .catch(err => console.error('Failed to load balance during edit:', err))
    .finally(() => {
      balanceLoading.value = false
      suppressBalanceWatch.value = false
      nextTick(() => { isSyncingScanEdit.value = false })
    })
}

function cancelEdit() {
  isSyncingScanEdit.value = true
  closeScanModal()
  isEditing.value = false
  editIndex.value = -1
  editSource.value = null
  pendingEditPermissions.value = null
  const currentHeader = {
    date: form.value.date,
    issuingToKey: form.value.issuingToKey,
    departmentId: form.value.departmentId,
    incomeCategoryId: form.value.incomeCategoryId
  }
  form.value = { ...getInitialForm(), ...currentHeader }
  stockBalances.value = []
  scannedSerials.value = []
  nextTick(() => { isSyncingScanEdit.value = false })
}

function deleteItem(index) {
  sivList.value.splice(index, 1)
  localSave()
}

async function saveToDatabase() {
  if (!sivList.value.length) return
  isSaving.value = true
  try {
    const first = sivList.value[0]
    const isOtherSave = isOtherTransfer.value || sivList.value.some((row) => row?.destinationBranchId)
    const receiverBranchId = isOtherSave
      ? (first.destinationBranchId || getSelectedOtherTransferBranchId())
      : null
    if (isOtherSave && !receiverBranchId) {
      alert('Please select a branch.')
      return
    }
    const payload = {
      date: first.date,
      departmentId: first.departmentId ?? null,
      incomeCategoryId: first.incomeCategoryId ?? null,
      companyId: companyId.value,
      branchId: branchId.value,
      created_by: userId.value,
      status: isOtherSave ? 0 : (sivList.value.some(i => i.approvedRequestItemId) ? 1 : 0),
      ...(isOtherSave ? { receiverBranchId } : {}),
      items: sivList.value.map(i => {
        let u = ''
        if (i.productId) {
          const pInfo = products.value.find(p => p.id == i.productId)
          u = (pInfo?.uom || pInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
        } else {
          const itemInfo = registeredItems.value.find(it => it.id == i.itemId)
          u = (itemInfo?.unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
        }

        // Unit detection: project uses "liter" and "K.G." styles.
        const isLiterOrKg = (
          u === 'l' ||
          u === 'liter' ||
          u === 'liters' ||
          u.startsWith('liter') ||
          u === 'kg' ||
          u === 'k.g' ||
          u === 'k.g.'
        )

        let quantityDetailsForApi = i.quantityDetails ?? null
        let quantityForApi = i.quantity
        if (isLiterOrKg && i.quantityDetails && i.variantDetails) {
          const qtyArr = i.quantityDetails.split(',').map(Number)
          const volArr = i.variantDetails.split(',').map(Number)
          const baseQtys = qtyArr.map((q, idx) => {
            const v = Number(volArr[idx] ?? volArr[0] ?? 0)
            if (!Number.isFinite(v) || v === 0) return q
            return (q * 1000) / v
          })
          quantityDetailsForApi = baseQtys.join(',')
          // Requirement: total quantity must be summed AFTER conversion
          quantityForApi = baseQtys.reduce((sum, n) => sum + (Number(n) || 0), 0)
        }

        return {
          itemId: i.itemId || null,
          productId: i.productId || null,
          quantity: quantityForApi,
          unitPrice: i.unitPrice ?? null,
          quantityDetails: quantityDetailsForApi,
          unitPriceDetails: i.unitPriceDetails ?? null,
          variantDetails: i.variantDetails ?? null,
          expiryDateDetails: i.expiryDateDetails ?? null,
          ...(!isOtherSave ? { barcodeResults: Array.isArray(i.barcodeResults) ? i.barcodeResults : [] } : {}),
          remark: i.remark,
          approvedRequestItemId: isOtherSave ? null : (i.approvedRequestItemId || null)
        }
      })
    }
    const endpoint = isOtherSave ? '/other-siv' : '/siv'
    const res = await apiFetch(endpoint, { method: 'POST', body: JSON.stringify(payload) })
    if (res.ok) {
      const responseData = await res.json().catch(() => ({}))
      const savedSivNo = responseData?.sivNo || responseData?.data?.sivNo || ''
      if (savedSivNo) {
        sivList.value = sivList.value.map((row) => ({ ...row, sivNo: savedSivNo }))
      }
      const docLabel = isOtherSave ? 'Other SIV' : 'SIV'
      alert(savedSivNo ? `${docLabel} saved successfully. SIV Number: ${savedSivNo}` : `${docLabel} saved successfully.`)

      // Trigger print before clearing the list
      setTimeout(async () => {
        if (confirm("Are you sure..........Print?")) {
          await generateSivPdf()
        }
        sivList.value = []
        issuingToLockKey.value = null
        localStorage.removeItem('Mina_siv_temp')
        form.value = { ...getInitialForm() }
        await Promise.all([
          loadApprovedItems(),
          loadPendingReceiveApprovals(),
          loadPendingReceiveItems(),
          loadNotApprovedOtherSivItems(),
          loadPendingSivEditAccessRows()
        ])
        // Refresh next SIV number for the next entry.
        await fetchNextSivNumberForDisplay()
      }, 500)
    } else {
      const err = await res.json()
      alert(res.status === 409 ? err.message : 'Error: ' + err.message)
    }
  } catch (err) { console.error(err) }
  finally { isSaving.value = false }
}

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'PNG'
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'PNG'
}

async function imageUrlToDataUrl(url) {
  if (!url) return { dataUrl: null, format: 'PNG' }
  try {
    const res = await fetch(url)
    if (!res.ok) return { dataUrl: null, format: 'PNG' }
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return {
      dataUrl,
      format: getImageFormatFromDataUrl(dataUrl)
    }
  } catch {
    return { dataUrl: null, format: 'PNG' }
  }
}

async function generateSivPdf() {
  const rows = flattenedSivList.value
  if (!rows.length) return

  const totals = {
    amount: Number(totalAmount.value || 0),
  }
  const meta = {
    sivNumber: withOtherSivPrefix(rows[0]?.sivNumber || form.value.sivNo || displayedSivNumber.value, 'SIV'),
    transactionDate: rows[0]?.date || form.value.date || new Date().toISOString().slice(0, 10),
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const leftMargin = 16
  const rightMargin = 8
  const topMargin = 8
  const headerBottomY = 28
  const totalPagesExp = '{total_pages_count_string}'
  const today = new Date().toLocaleDateString()
  const { dataUrl: logoDataUrl, format: logoFormat } = await imageUrlToDataUrl(String(printCompanyLogo.value || '').trim())

  const includeBranchCol = isOtherTransfer.value
  const tableBody = rows.map((row, idx) => {
    const cols = [
      String(idx + 1),
      withOtherSivPrefix(row.sivNumber, '-') || '-',
      row.date || '-'
    ]
    if (includeBranchCol) cols.push(row.branchName || '-')
    cols.push(
      row.departmentName || '-',
      row.categoryName || '-',
      row.itemName || '-',
      String(row.displayExpiryDate ?? '-'),
      String(row.displayVolume ?? '-'),
      String(row.displayQuantity ?? '-'),
      String(row.displayUnitPrice ?? '-'),
      String(row.displayTotalPrice ?? '-'),
      row.remark || '-'
    )
    return cols
  })

  autoTable(doc, {
    head: [[
      '#', 'Siv No', 'Date', ...(includeBranchCol ? ['Branch'] : []), 'Department', 'Category', 'Item', 'Expiry Date', 'Volume', 'Qty', 'Unit Price', 'Total price', 'Remark'
    ]],
    body: tableBody,
    startY: headerBottomY + 2,
    theme: 'grid',
    margin: { left: leftMargin, right: rightMargin, top: headerBottomY + 2, bottom: 12 },
    styles: {
      fontSize: 9.0,
      cellPadding: 1.2,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    },
    bodyStyles: {
      fillColor: [255, 255, 255]
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    didDrawPage: () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, logoFormat, leftMargin, topMargin, 14, 10, undefined, 'FAST')
      }

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text(String(printCompanyName.value || 'Company'), pageWidth / 2, topMargin + 3, { align: 'center' })
      doc.setFontSize(11)
      doc.text(isOtherTransfer.value ? 'Other Store Issue Voucher' : 'Store Issue Voucher', pageWidth / 2, topMargin + 8, { align: 'center' })

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(`Date: ${today}`, pageWidth - rightMargin, topMargin + 6, { align: 'right' })

      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.2)
      doc.line(leftMargin, headerBottomY, pageWidth - rightMargin, headerBottomY)

      const pageNo = doc.getNumberOfPages()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.text(`Page ${pageNo} of ${totalPagesExp}`, pageWidth - rightMargin, pageHeight - 5, { align: 'right' })
    }
  })

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  const finalY = (doc.lastAutoTable?.finalY || (headerBottomY + 2)) + 8
  const valueX = pageWidth - rightMargin
  const labelX = valueX - 32
  const lineGap = 6

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setLineWidth(0.2)
  doc.line(labelX - 18, finalY + 0.5, valueX, finalY + 0.5)
  doc.setFont('helvetica', 'bold')
  doc.text('Total Amount:', labelX, finalY + 5, { align: 'right' })
  doc.text(Number(totals.amount || 0).toFixed(2), valueX, finalY + 5, { align: 'right' })

  const footerY = Math.max(finalY + (lineGap * 6), pageHeight - 28)
  const firstX = leftMargin + 2
  const secondX = (pageWidth / 2) - 48
  const thirdX = (pageWidth / 2) + 2
  const fourthX = pageWidth - rightMargin - 42
  const lineW = 40

  doc.setFont('helvetica', 'normal')

  // Issued By
  doc.line(firstX, footerY, firstX + lineW, footerY)
  doc.text('Issued By', firstX + (lineW / 2), footerY + 5, { align: 'center' })

  // Checked By
  doc.line(secondX, footerY, secondX + lineW, footerY)
  doc.text('Checked By', secondX + (lineW / 2), footerY + 5, { align: 'center' })

  // Received By
  doc.line(thirdX, footerY, thirdX + lineW, footerY)
  doc.text('Received By', thirdX + (lineW / 2), footerY + 5, { align: 'center' })

  // Approved By
  doc.line(fourthX, footerY, fourthX + lineW, footerY)
  doc.text('Approved By', fourthX + (lineW / 2), footerY + 5, { align: 'center' })

  const safeSivNo = String(meta.sivNumber || 'SIV').replace(/[\\/:*?"<>|]/g, '-').trim()
  const safeDate = String(meta.transactionDate || new Date().toISOString().slice(0, 10)).replace(/[\\/:*?"<>|]/g, '-').trim()
  const filename = `${safeSivNo}-${safeDate}.pdf`
  doc.save(filename)
}

function localSave() { localStorage.setItem('Mina_siv_temp', JSON.stringify(sivList.value)) }
function localLoad() {
  const saved = localStorage.getItem('Mina_siv_temp')
  if (saved) sivList.value = JSON.parse(saved)
}

async function restoreOtherTransferFromTempList() {
  if (!Array.isArray(sivList.value) || sivList.value.length === 0) return
  const first = sivList.value[0]
  const destId = first?.destinationBranchId
  if (!destId) return
  isOtherTransfer.value = true
  const key = `branch-${destId}`
  otherTransferTargetKey.value = key
  await loadOtherTransferBranches()
  await loadOtherTransferDepartments(key)
}

function getSelectableName(type, id) {
  if (type === 'product') {
    return products.value.find(p => p.id == id)?.name || '-'
  }
  return registeredItems.value.find(i => i.id == id)?.itemName || '-'
}
function getCategoryName(id) { return categories.value.find(c => c.id === id)?.name || '-' }
function getDepartmentName(id) {
  const fromHome = departments.value.find(d => d.id === id)
  if (fromHome?.name) return fromHome.name
  const fromOther = otherTransferDepartments.value.find(d => d.id === id)
  return fromOther?.name || '-'
}
function getIssuingToName(departmentId, incomeCategoryId, incomeCategoryName = null) {
  if (departmentId != null && departmentId !== '') {
    return getDepartmentName(departmentId)
  }
  const embeddedName = String(incomeCategoryName || '').trim()
  if (embeddedName) return embeddedName
  if (incomeCategoryId != null && incomeCategoryId !== '') {
    const cat = incomeCategories.value.find((c) => Number(c.id) === Number(incomeCategoryId))
    return cat?.name || '-'
  }
  return '-'
}
function mapReceiveListRow(row) {
  const incomeCategoryId = row?.incomeCategoryId ?? row?.income_category_id ?? null
  const incomeCategoryName = row?.incomeCategory?.name ?? row?.incomeCategoryName ?? null
  const departmentId = row?.departmentId ?? row?.department_id ?? null
  const department = (incomeCategoryId != null && incomeCategoryId !== '')
    ? getIssuingToName(null, incomeCategoryId, incomeCategoryName)
    : (String(row?.department || '').trim() || getIssuingToName(departmentId, null))
  return { ...row, department }
}

onMounted(async () => {
  window.addEventListener('message', handleHostedBarcodeScannerMessage)
  await loadData()
  await refreshSivApprovalRequiredSetting()
  localLoad()
  await restoreOtherTransferFromTempList()
  await loadApprovedItems()
  await loadPendingReceiveApprovals()
  await loadPendingReceiveItems()
  await loadNotApprovedOtherSivItems()
  await loadPendingSivEditAccessRows()
  await fetchNextSivNumberForDisplay()
})

onUnmounted(() => {
  window.removeEventListener('message', handleHostedBarcodeScannerMessage)
  if (scannerCaptureTimer) clearTimeout(scannerCaptureTimer)
  if (receiveApprovalToastTimer) clearTimeout(receiveApprovalToastTimer)
  hostedBarcodeScannerWindow?.close?.()
})

watch(
  () => sivList.value,
  (list) => {
    // Capture the first issuing target ONCE when the temp SIV list appears,
    // and keep it until the temp list is cleared (after saving).
    if (!Array.isArray(list) || list.length === 0) {
      issuingToLockKey.value = null
      return
    }

    if (issuingToLockKey.value == null) {
      const first = list[0]
      issuingToLockKey.value = toIssuingToKey(
        first?.departmentId ?? first?.department_id ?? null,
        first?.incomeCategoryId ?? first?.income_category_id ?? null
      )
    }

    if (issuingToLockKey.value != null && form.value.issuingToKey !== issuingToLockKey.value) {
      form.value.issuingToKey = issuingToLockKey.value
    }
  },
  { immediate: true }
)

// Pre-fill form from approved request row click
function addApprovedToList(item) {
  const depId = item.departmentId || form.value.departmentId || null
  form.value.departmentId = depId
  form.value.incomeCategoryId = null
  form.value.issuingToKey = depId ? `department-${depId}` : form.value.issuingToKey
  form.value.categoryId = item.categoryId || null
  form.value.selectedItemKey = item.itemId ? `item:${item.itemId}` : null
  form.value.itemId = item.itemId
  form.value.productId = null
  form.value.remark = item.remark || ''
  form.value.approvedQuantity = item.approvedQuantity || 0
  form.value.approvedRequestItemId = item.id
}

</script>

<style scoped>
/* Phone: balance table scrolls horizontally; md+ gives balance the remaining row space */
.siv-balance-scroll {
  overflow-x: auto;
}

.siv-balance-table {
  min-width: 32rem;
}

@media (min-width: 768px) {
  .siv-field-item,
  .siv-field-remark {
    flex: 0 0 auto;
    width: 9.5rem;
    max-width: 11rem;
  }

  .siv-field-approved {
    flex: 0 0 auto;
    width: 8rem;
    max-width: 9rem;
  }

  .siv-field-balance {
    flex: 1 1 0;
    min-width: 0;
  }

  .siv-balance-scroll {
    overflow-x: visible;
    overflow: visible;
  }

  .siv-balance-table {
    min-width: 0;
    width: 100%;
    table-layout: fixed;
  }

  .siv-balance-table th,
  .siv-balance-table td {
    white-space: normal;
    word-break: break-word;
  }
}

@media (min-width: 1024px) {
  .siv-field-item,
  .siv-field-remark {
    width: 10.5rem;
    max-width: 12rem;
  }

  .siv-ot-layout .siv-field-remark {
    flex: 0 0 auto;
    width: 10.5rem;
    max-width: 12rem;
  }

  .siv-ot-layout .siv-field-balance {
    flex: 1 1 0;
    min-width: 0;
  }
}

@media (max-width: 1023px) {
  .siv-ot-layout .siv-field-remark,
  .siv-ot-layout .siv-field-balance,
  .siv-ot-layout .siv-field-approved {
    flex: 1 1 auto;
    width: 100%;
    max-width: none;
  }
}

table { font-family: 'Segoe UI', sans-serif; font-size: 12px; color: #1e293b; width: 100%; border-collapse: collapse; }
th, td { padding: 3px 4px; text-align: left; border-bottom: 1px solid #e2e8f0; font-size: 12px; white-space: nowrap; }
th { background: #f1f5f9; cursor: default; font-weight: 600; font-size: 12px !important; color: inherit !important; }
tbody tr:hover { background: #f1f5f9; }
.status-pill { padding: 2px 6px; border-radius: 12px; font-size: 0.65rem; font-weight: bold; display: inline-block; }
.active { background: #dcfce7; color: #166534; }
.inactive { background: #f1f5f9; color: #64748b; }

input:disabled,
select:disabled {
  cursor: not-allowed;
  background-color: #f8fafc;
}

input:disabled:hover,
select:disabled:hover {
  cursor: not-allowed;
  border-color: #ef4444;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 6px !important;
    text-align: left !important;
    color: #000 !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.delbtn {
  background: none;
  margin-left: 10px;
  border: none;
  color: red;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}
</style>
